import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { AdminTemplatesSection } from '../components/admin/AdminTemplatesSection.jsx';
import { AdminFinanceSection } from '../components/admin/AdminFinanceSection.jsx';
import { AdminOverviewSection } from '../components/admin/AdminOverviewSection.jsx';
import {
  AdminSubscriptionPayButton,
  AdminSubscriptionPortalLink,
} from '../components/admin/AdminSubscriptionPortalActions.jsx';
import { AdminTenantReportModal } from '../components/admin/AdminTenantReportModal.jsx';
import { TenantOperationalBadge } from '../components/admin/TenantOperationalBadge.jsx';
import { isReservedStoreSlug } from '../config/reservedSlugs.js';
import { STORE_TIMEZONE_IANA_SUGGESTIONS } from '../config/storeTimezones.js';
import { toSlug } from '../utils/slug.js';
import { resolveTenantOperationalHealth } from '../utils/tenantOperationalHealth.js';

/** `YYYY-MM-DD HH:mm:ss` → valor para `<input type="datetime-local" />` */
function toDatetimeLocalValue(mysql) {
  if (!mysql) return '';
  const s = String(mysql).replace(' ', 'T').trim();
  return s.length >= 16 ? s.slice(0, 16) : s;
}

function formatBrlCompact(n) {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(n));
}

function billingPlanMapFrom(plansArr) {
  const m = Object.create(null);
  for (const p of plansArr || []) {
    if (p?.plan_key) m[p.plan_key] = p;
  }
  return m;
}

function tenantResolvedMonthlyAmount(tenant, catalogByKey) {
  const rawOv = tenant?.subscription_price_override_brl;
  if (rawOv != null && String(rawOv).trim() !== '') {
    const ov = Number(rawOv);
    if (!Number.isNaN(ov)) return ov;
  }
  const k =
    tenant?.subscription_plan_key && String(tenant.subscription_plan_key).trim()
      ? String(tenant.subscription_plan_key).trim()
      : 'gold';
  const row = catalogByKey[k];
  const base = row?.monthly_amount_brl != null ? Number(row.monthly_amount_brl) : null;
  const amt = base != null ? base : null;
  if (amt == null || Number.isNaN(amt)) return null;
  const dp = Number(tenant?.subscription_discount_percent || 0);
  if (Number.isFinite(dp) && dp > 0) {
    return Math.round(amt * (100 - dp)) / 100;
  }
  return amt;
}

/** Dinheiro em texto tipo `197`, `197.5`, `197,50`, `1.234,56`. */
function parseLooseBRLMoney(input) {
  const s = String(input ?? '').trim().replace(/\s/g, '');
  if (s === '') return null;
  if (s.includes(',')) return Number(s.replace(/\./g, '').replace(',', '.'));
  const n = Number(s);
  return Number.isFinite(n) ? n : Number.NaN;
}

/** Base típica `…/integration-php/public/index.php/api` (override: `VITE_ERP_INTEGRATION_URL`). */
function buildErpIntegrationBaseUrl() {
  const raw =
    typeof import.meta.env?.VITE_ERP_INTEGRATION_URL === 'string' ? import.meta.env.VITE_ERP_INTEGRATION_URL.trim() : '';
  if (raw) return raw.replace(/\/?$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin.replace(/\/?$/, '')}/integration-php/public/index.php/api`;
  }
  return '/integration-php/public/index.php/api';
}

/** Métodos HTTP aceites no filtro de `GET /admin/erp-inbound-logs` (query `erp_req_method`; alinhado à API). */
const ERP_INBOUND_HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

/** @param {unknown} tenants @param {string} query */
function filterTenantsForLogPicker(tenants, query) {
  const q = String(query || '').trim().toLowerCase();
  const list = Array.isArray(tenants) ? tenants : [];
  const sorted = [...list].sort((a, b) =>
    String(a.nome || '').localeCompare(String(b.nome || ''), 'pt', { sensitivity: 'base' }),
  );
  if (!q) return sorted;
  return sorted.filter((t) => {
    const nome = String(t.nome || '').toLowerCase();
    const slug = String(t.slug || '').toLowerCase();
    const id = String(t.id ?? '');
    return nome.includes(q) || slug.includes(q) || id.includes(q);
  });
}

/** Resumo legível de meta de notificação (WA/e-mail). */
function formatNotifyLogMeta(meta) {
  if (!meta || typeof meta !== 'object') return '';
  const m = /** @type {Record<string, unknown>} */ (meta);
  const parts = [];
  if (m.order_id != null) parts.push(`pedido #${m.order_id}`);
  if (typeof m.order_code === 'string' && m.order_code) parts.push(m.order_code);
  if (typeof m.phase === 'string') parts.push(`fase=${m.phase}`);
  if (typeof m.hook === 'string') parts.push(`hook=${m.hook}`);
  if (typeof m.wa_source === 'string' && m.wa_source) parts.push(`token=${m.wa_source}`);
  if (typeof m.template_event === 'string') parts.push(`tpl=${m.template_event}`);
  if (m.prev_status != null && m.new_status != null) {
    parts.push(`${m.prev_status}→${m.new_status}`);
  }
  const waCust = m.wa_customer;
  if (waCust && typeof waCust === 'object') {
    const w = /** @type {Record<string, unknown>} */ (waCust);
    parts.push(`wa_cli sent=${w.sent ?? 0}/${w.attempted ?? 0} (${w.reason ?? '—'})`);
  }
  const waStore = m.wa_store;
  if (waStore && typeof waStore === 'object') {
    const w = /** @type {Record<string, unknown>} */ (waStore);
    parts.push(`wa_loja sent=${w.sent ?? 0}/${w.attempted ?? 0} (${w.reason ?? '—'})`);
  }
  const wa = m.wa;
  if (wa && typeof wa === 'object') {
    const w = /** @type {Record<string, unknown>} */ (wa);
    parts.push(`wa sent=${w.sent ?? 0}/${w.attempted ?? 0} (${w.reason ?? '—'})`);
  }
  if (m.customer_sent != null) parts.push(`cli_sent=${m.customer_sent}`);
  if (m.store_sent != null) parts.push(`loja_sent=${m.store_sent}`);
  if (typeof m.error === 'string' && m.error) parts.push(`err=${m.error}`);
  if (parts.length) return parts.join(' · ');
  return JSON.stringify(meta);
}

export function AdminPage() {
  const [tab, setTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [logs, setLogs] = useState([]);
  /** Separador Logs: quantas linhas pedir a GET /admin/logs (API aceita até 2500). */
  const [auditLogLimit, setAuditLogLimit] = useState(500);
  const [auditLogTenantFilter, setAuditLogTenantFilter] = useState('');
  const [auditLogTenantSearch, setAuditLogTenantSearch] = useState('');
  const [auditLogTenantPickerOpen, setAuditLogTenantPickerOpen] = useState(false);
  /** WhatsApp / e-mail de pedidos (audit_logs action notify.*). */
  const [notifyLogs, setNotifyLogs] = useState([]);
  const [notifyLogsErr, setNotifyLogsErr] = useState('');
  const [notifyLogLimit, setNotifyLogLimit] = useState(400);
  const [notifyLogTenantFilter, setNotifyLogTenantFilter] = useState('');
  const [notifyLogTenantSearch, setNotifyLogTenantSearch] = useState('');
  const [notifyLogTenantPickerOpen, setNotifyLogTenantPickerOpen] = useState(false);
  /** Amostra de erp_integration_inbound_logs no mesmo separador (evita só ir ao tab ERP). */
  const [logsTabErpPreview, setLogsTabErpPreview] = useState([]);
  /** Erros por bloco no separador «Logs» (evita um falhar e esconder o outro via Promise.all). */
  const [logsTabAuditErr, setLogsTabAuditErr] = useState('');
  const [logsTabErpErr, setLogsTabErpErr] = useState('');
  const [logsTabErpSchemaNote, setLogsTabErpSchemaNote] = useState('');
  const [erpInbound, setErpInbound] = useState([]);
  /** Aviso da API quando a listagem ERP usa esquema legado (sem colunas enriquecidas). */
  const [erpInboundSchemaNote, setErpInboundSchemaNote] = useState('');
  const [erpTenantFilter, setErpTenantFilter] = useState('');
  const [erpMethodFilter, setErpMethodFilter] = useState('');
  const [erpTenantSearch, setErpTenantSearch] = useState('');
  const [erpTenantPickerOpen, setErpTenantPickerOpen] = useState(false);
  const [erpExpandedId, setErpExpandedId] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [formUser, setFormUser] = useState({ name: '', email: '', whatsapp: '', password: '', role: 'user' });
  const [formTenant, setFormTenant] = useState({
    user_id: '',
    nome: '',
    slug: '',
    subscription_discount_percent: '',
    subscription_billing_exempt_erp: false,
  });

  const [editUser, setEditUser] = useState(null);
  const [editUserBusy, setEditUserBusy] = useState(false);
  const [editUserErr, setEditUserErr] = useState('');
  const [editUserForm, setEditUserForm] = useState({
    name: '',
    email: '',
    whatsapp: '',
    password: '',
    role: 'user',
    billing_plan: 'essential_trial',
    trial_ends_at: '',
    blocked: false,
  });

  const [reportTenant, setReportTenant] = useState(null);
  const [editTenant, setEditTenant] = useState(null);
  const [editTenantBusy, setEditTenantBusy] = useState(false);
  const [editTenantErr, setEditTenantErr] = useState('');
  /** Resposta de GET /admin/tenants/:id/credentials (super_admin). */
  const [adminTenantCredentials, setAdminTenantCredentials] = useState(null);
  const [adminTenantCredentialsBusy, setAdminTenantCredentialsBusy] = useState(false);
  const [adminTenantCredentialsErr, setAdminTenantCredentialsErr] = useState('');
  const [adminErpRegenBusy, setAdminErpRegenBusy] = useState(false);
  /** Último par gerado por POST erp-credentials (mostrar até fechar ou gerar de novo). */
  const [adminErpRegenerated, setAdminErpRegenerated] = useState(null);
  const [clearDataModal, setClearDataModal] = useState(null);
  const [clearDataOpts, setClearDataOpts] = useState({ orders: false, customers: false, products: false });
  const [clearDataConfirmIrreversible, setClearDataConfirmIrreversible] = useState(false);
  const [clearDataBusy, setClearDataBusy] = useState(false);
  const [clearDataErr, setClearDataErr] = useState('');
  const [editTenantForm, setEditTenantForm] = useState({
    nome: '',
    slug: '',
    user_id: '',
    status: 'active',
    payment_online_enabled: false,
    block_customer_public_registration: false,
    require_adult_confirmation: false,
    catalog_only_mode: false,
    catalog_no_photo_mode: false,
    block_sale_zero_stock: false,
    pickup_only_mode: false,
    mercadopago_access_token: '',
    clearMercadoPagoGateway: false,
    mercadopago_client_id: '',
    mercadopago_client_secret: '',
    clearMercadoPagoOAuthSecret: false,
    erp_client_id: '',
    erp_client_secret: '',
    clearErpSecret: false,
    subscription_plan_key: 'gold',
    subscription_price_override_brl: '',
    subscription_discount_percent: '',
    subscription_billing_exempt_erp: false,
    contact_address: '',
    contact_whatsapp: '',
    contact_google_maps_url: '',
    contact_facebook_url: '',
    contact_instagram_url: '',
    contact_youtube_url: '',
    contact_opening_hours: '',
    timezone: '',
    software_house_id: '',
    wa_sh_api_token: '',
    clearWaShToken: false,
  });
  const [shForm, setShForm] = useState({
    nome: '',
    endpoint_url: '',
    bearer_token: '',
    mercadopago_access_token: '',
  });
  const [shBusy, setShBusy] = useState(false);
  /** @type{null | { id: number, nome: string, endpoint_url: string, bearer_token: string, bearer_was_configured?: number, mercadopago_access_token: string, mp_was_configured?: number, clearMpToken?: boolean }} */
  const [editSh, setEditSh] = useState(null);
  const [shEditBusy, setShEditBusy] = useState(false);

  const [softwareHouses, setSoftwareHouses] = useState([]);
  const [saasPlans, setSaasPlans] = useState([]);
  const [planDraftByKey, setPlanDraftByKey] = useState({});
  const [planRowSaveBusy, setPlanRowSaveBusy] = useState('');
  /** A gerar próxima cobrança SaaS — só existe no admin; pagamento fica em /app/mensalidade */
  const [saasGenBusyTenantId, setSaasGenBusyTenantId] = useState(null);

  /** Combobox «Criar empresa» — filtro e lista de responsáveis. */
  const [ownerPickerOpen, setOwnerPickerOpen] = useState(false);
  const [ownerPickerQuery, setOwnerPickerQuery] = useState('');
  const ownerPickerWrapRef = useRef(null);
  const erpTenantPickerRef = useRef(null);
  const notifyLogTenantPickerRef = useRef(null);
  const auditLogTenantPickerRef = useRef(null);
  /** Resultados de GET /admin/users?q= (2+ caracteres); null = usar filtro local em `users`. */
  const [ownerPickRemoteList, setOwnerPickRemoteList] = useState(null);
  const [ownerPickRemoteBusy, setOwnerPickRemoteBusy] = useState(false);

  const ownerPickerFiltered = useMemo(() => {
    const q = ownerPickerQuery.trim();
    const qL = q.toLowerCase();
    if (q.length >= 2 && ownerPickRemoteList !== null) {
      return ownerPickRemoteList;
    }
    const arr = Array.isArray(users) ? users : [];
    if (!q) return arr.slice(0, 100);
    return arr
      .filter((u) => {
        const id = String(u.id ?? '');
        const name = String(u.name || '').toLowerCase();
        const email = String(u.email || '').toLowerCase();
        const wa = String(u.whatsapp || '').toLowerCase();
        return id.includes(qL) || name.includes(qL) || email.includes(qL) || wa.includes(qL);
      })
      .slice(0, 100);
  }, [users, ownerPickerQuery, ownerPickRemoteList]);

  useEffect(() => {
    const q = ownerPickerQuery.trim();
    if (q.length < 2) {
      setOwnerPickRemoteList(null);
      setOwnerPickRemoteBusy(false);
      return undefined;
    }
    setOwnerPickRemoteList(null);
    let cancelled = false;
    const id = window.setTimeout(async () => {
      setOwnerPickRemoteBusy(true);
      try {
        const d = await api('/admin/users?q=' + encodeURIComponent(q));
        if (!cancelled) setOwnerPickRemoteList(d.users || []);
      } catch {
        if (!cancelled) setOwnerPickRemoteList([]);
      } finally {
        if (!cancelled) setOwnerPickRemoteBusy(false);
      }
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [ownerPickerQuery]);

  const erpTenantPickerList = useMemo(() => filterTenantsForLogPicker(tenants, erpTenantSearch), [tenants, erpTenantSearch]);

  const notifyLogTenantPickerList = useMemo(
    () => filterTenantsForLogPicker(tenants, notifyLogTenantSearch),
    [tenants, notifyLogTenantSearch],
  );

  const auditLogTenantPickerList = useMemo(
    () => filterTenantsForLogPicker(tenants, auditLogTenantSearch),
    [tenants, auditLogTenantSearch],
  );

  const provisionSelectedUser = useMemo(
    () => (users || []).find((u) => String(u.id) === String(formTenant.user_id)) || null,
    [users, formTenant.user_id],
  );

  useEffect(() => {
    if (!ownerPickerOpen) return undefined;
    function onDocMouseDown(e) {
      const el = ownerPickerWrapRef.current;
      if (el && !el.contains(e.target)) setOwnerPickerOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [ownerPickerOpen]);

  useEffect(() => {
    if (!erpTenantPickerOpen) return undefined;
    function onDocMouseDown(e) {
      const el = erpTenantPickerRef.current;
      if (el && !el.contains(e.target)) setErpTenantPickerOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [erpTenantPickerOpen]);

  useEffect(() => {
    if (!notifyLogTenantPickerOpen) return undefined;
    function onDocMouseDown(e) {
      const el = notifyLogTenantPickerRef.current;
      if (el && !el.contains(e.target)) setNotifyLogTenantPickerOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [notifyLogTenantPickerOpen]);

  useEffect(() => {
    if (!auditLogTenantPickerOpen) return undefined;
    function onDocMouseDown(e) {
      const el = auditLogTenantPickerRef.current;
      if (el && !el.contains(e.target)) setAuditLogTenantPickerOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [auditLogTenantPickerOpen]);

  async function loadBillingPlansCatalog() {
    try {
      const bp = await api('/admin/billing/plans');
      const plans = bp.plans || [];
      setSaasPlans(plans);
      const nextDraft = {};
      for (const p of plans) {
        const amt = Number(p.monthly_amount_brl);
        nextDraft[p.plan_key] = {
          title: String(p.title ?? ''),
          monthly_amount_brl: Number.isNaN(amt) ? '' : amt.toFixed(2).replace('.', ','),
          lp_name: String(p.lp_name ?? ''),
          subtitle: String(p.subtitle ?? ''),
          price_label: String(p.price_label ?? ''),
          cadence: String(p.cadence ?? ''),
          bulletsText: Array.isArray(p.bullets) ? p.bullets.join('\n') : '',
          caveat: p.caveat == null ? '' : String(p.caveat),
          featured: Boolean(p.featured),
        };
      }
      setPlanDraftByKey(nextDraft);
    } catch (err) {
      setSaasPlans([]);
      setPlanDraftByKey({});
      const code = err?.data?.code;
      const msg = String(err?.message || '');
      if (code === 'SCHEMA_OUTDATED' || msg.includes('SCHEMA_OUTDATED')) {
        setError(
          'Schema desatualizado para o catálogo de planos. Importe database/0055_billing_plan_lp_copy.sql no MySQL e recarregue.',
        );
      }
    }
  }

  async function reload() {
    setError('');
    try {
      if (tab === 'users') {
        const d = await api('/admin/users');
        setUsers(d.users || []);
      }
      if (tab === 'plans') {
        await loadBillingPlansCatalog();
      }
      if (tab === 'tenants') {
        const d = await api('/admin/tenants');
        setTenants(d.tenants || []);
        try {
          const u = await api('/admin/users');
          setUsers(u.users || []);
        } catch {
          setUsers([]);
        }
        try {
          const sh = await api('/admin/software-houses');
          setSoftwareHouses(sh.software_houses || []);
        } catch {
          setSoftwareHouses([]);
        }
        await loadBillingPlansCatalog();
      }
      if (tab === 'logs') {
        const limAudit = Math.min(2500, Math.max(1, Number(auditLogLimit) || 500));
        const limNotify = Math.min(2500, Math.max(1, Number(notifyLogLimit) || 400));
        setLogsTabAuditErr('');
        setLogsTabErpErr('');
        setLogsTabErpSchemaNote('');
        setNotifyLogsErr('');
        try {
          const td = await api('/admin/tenants');
          setTenants(td.tenants || []);
        } catch {
          /* mantém lista em memória */
        }
        const auditQs = new URLSearchParams();
        auditQs.set('limit', String(limAudit));
        if (auditLogTenantFilter.trim() !== '') auditQs.set('tenant_id', auditLogTenantFilter.trim());
        try {
          const dAudit = await api(`/admin/logs?${auditQs.toString()}`);
          setLogs(dAudit.logs || []);
        } catch (e) {
          setLogs([]);
          setLogsTabAuditErr(e.message || 'Falha ao carregar auditoria.');
        }
        const notifyQs = new URLSearchParams();
        notifyQs.set('limit', String(limNotify));
        notifyQs.set('category', 'notify');
        if (notifyLogTenantFilter.trim() !== '') notifyQs.set('tenant_id', notifyLogTenantFilter.trim());
        try {
          const dNotify = await api(`/admin/logs?${notifyQs.toString()}`);
          setNotifyLogs(dNotify.logs || []);
        } catch (e) {
          setNotifyLogs([]);
          setNotifyLogsErr(e.message || 'Falha ao carregar notificações de pedidos.');
        }
        try {
          const dErp = await api('/admin/erp-inbound-logs?limit=400');
          setLogsTabErpPreview(dErp.entries || []);
          if (typeof dErp.schema_note === 'string' && dErp.schema_note.trim() !== '') {
            setLogsTabErpSchemaNote(dErp.schema_note.trim());
          }
        } catch (e) {
          setLogsTabErpPreview([]);
          setLogsTabErpErr(e.message || 'Falha ao carregar integração ERP.');
        }
      }
      if (tab === 'erpLogs') {
        setErpInboundSchemaNote('');
        let tenantList = tenants;
        try {
          const td = await api('/admin/tenants');
          tenantList = td.tenants || [];
          setTenants(tenantList);
        } catch {
          /* mantém lista já em memória */
        }
        const pf = new URLSearchParams();
        pf.set('limit', '800');
        if (erpTenantFilter.trim() !== '') pf.set('tenant_id', erpTenantFilter.trim());
        if (erpMethodFilter.trim() !== '') pf.set('erp_req_method', erpMethodFilter.trim().toUpperCase());
        const q = `?${pf.toString()}`;
        const tid = erpTenantFilter.trim();
        if (tid && !erpTenantPickerOpen) {
          const hit = tenantList.find((t) => String(t.id) === tid);
          if (hit) setErpTenantSearch(`${hit.nome} (#${hit.id})`);
        }
        if (!tid) setErpTenantSearch('');
        try {
          const d = await api(`/admin/erp-inbound-logs${q}`);
          setErpInbound(d.entries || []);
          if (typeof d.schema_note === 'string' && d.schema_note.trim() !== '') {
            setErpInboundSchemaNote(d.schema_note.trim());
          }
        } catch (e) {
          setErpInbound([]);
          setErpInboundSchemaNote('');
          setError(e.message || 'Falha ao carregar log ERP.');
        }
      }
      if (tab === 'templates') {
        const u = await api('/admin/users');
        setUsers(u.users || []);
      }
      if (tab === 'softwareHouses') {
        try {
          const sh = await api('/admin/software-houses');
          setSoftwareHouses(sh.software_houses || []);
        } catch {
          setSoftwareHouses([]);
        }
      }
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    reload();
  }, [tab, auditLogLimit, auditLogTenantFilter, notifyLogLimit, notifyLogTenantFilter, erpTenantFilter, erpMethodFilter]);

  useEffect(() => {
    setSuccessMsg('');
  }, [tab]);

  useEffect(() => {
    if (!editTenant) {
      setAdminTenantCredentials(null);
      setAdminTenantCredentialsErr('');
      setAdminTenantCredentialsBusy(false);
      setAdminErpRegenBusy(false);
      setAdminErpRegenerated(null);
    }
  }, [editTenant]);

  function openEditUser(u) {
    setEditUserErr('');
    setEditUser(u);
    setEditUserForm({
      name: u.name ?? '',
      email: u.email ?? '',
      whatsapp: u.whatsapp ?? '',
      password: '',
      role: u.role === 'super_admin' ? 'super_admin' : 'user',
      billing_plan: u.billing_plan === 'gold' ? 'gold' : 'essential_trial',
      trial_ends_at: toDatetimeLocalValue(u.trial_ends_at),
      blocked: Boolean(u.blocked),
    });
  }

  function openClearDataModal(t) {
    setSuccessMsg('');
    setClearDataErr('');
    setClearDataOpts({ orders: false, customers: false, products: false });
    setClearDataConfirmIrreversible(false);
    setClearDataModal(t);
  }

  function closeClearDataModal() {
    if (clearDataBusy) return;
    setClearDataModal(null);
    setClearDataErr('');
    setClearDataConfirmIrreversible(false);
  }

  async function submitClearData(ev) {
    ev.preventDefault();
    if (!clearDataModal) return;
    setClearDataErr('');
    setClearDataBusy(true);
    try {
      const res = await api(`/admin/tenants/${clearDataModal.id}/clear-data`, {
        method: 'POST',
        body: JSON.stringify({
          orders: clearDataOpts.orders,
          customers: clearDataOpts.customers,
          products: clearDataOpts.products,
        }),
      });
      const skipped = Number(res?.customers_skipped_cross_store ?? 0);
      const deletedCust = Number(res?.customers_deleted ?? 0);
      const imgDel = Number(res?.product_images_deleted ?? 0);
      let msg = 'Dados limpos com sucesso.';
      if (clearDataOpts.customers && skipped > 0) {
        msg += ` ${skipped} cliente(s) não foi(ram) apagado(s) por terem o mesmo e-mail ou telefone que um cliente com pedidos noutra loja.`;
      }
      if (clearDataOpts.customers && deletedCust > 0) {
        msg += ` ${deletedCust} cliente(s) removido(s) desta loja.`;
      }
      if (clearDataOpts.products && imgDel > 0) {
        msg += ` ${imgDel} ficheiro(s) de foto removido(s) do disco.`;
      }
      setSuccessMsg(msg);
      closeClearDataModal();
      await reload();
    } catch (e) {
      setClearDataErr(e.message);
    } finally {
      setClearDataBusy(false);
    }
  }

  function openEditTenant(t) {
    setEditTenantErr('');
    setEditTenant(t);
    setEditTenantForm({
      nome: t.nome ?? '',
      slug: t.slug ?? '',
      user_id: String(t.user_id ?? ''),
      status: t.status === 'blocked' ? 'blocked' : 'active',
      payment_online_enabled: Boolean(Number(t.payment_online_enabled)),
      block_customer_public_registration: Boolean(Number(t.block_customer_public_registration || 0)),
      require_adult_confirmation: Boolean(Number(t.require_adult_confirmation || 0)),
      catalog_only_mode: Boolean(Number(t.catalog_only_mode || 0)),
      catalog_no_photo_mode: Boolean(Number(t.catalog_no_photo_mode || 0)),
      block_sale_zero_stock: Boolean(Number(t.block_sale_zero_stock || 0)),
      pickup_only_mode: Boolean(Number(t.pickup_only_mode || 0)),
      mercadopago_access_token: '',
      clearMercadoPagoGateway: false,
      mercadopago_client_id: t.mercadopago_client_id != null ? String(t.mercadopago_client_id) : '',
      mercadopago_client_secret: '',
      clearMercadoPagoOAuthSecret: false,
      erp_client_id: t.erp_client_id != null ? String(t.erp_client_id) : '',
      erp_client_secret: '',
      clearErpSecret: false,
      subscription_plan_key:
        t.subscription_plan_key != null && String(t.subscription_plan_key).trim()
          ? String(t.subscription_plan_key).trim()
          : 'gold',
      subscription_price_override_brl:
        t.subscription_price_override_brl != null && String(t.subscription_price_override_brl).trim() !== ''
          ? Number(t.subscription_price_override_brl).toFixed(2).replace('.', ',')
          : '',
      subscription_discount_percent:
        t.subscription_discount_percent != null && Number(t.subscription_discount_percent) > 0
          ? Number(t.subscription_discount_percent).toFixed(2).replace('.', ',')
          : '',
      subscription_billing_exempt_erp: Boolean(Number(t.subscription_billing_exempt_erp || 0)),
      contact_address: String(t.contact_address || ''),
      contact_whatsapp: String(t.contact_whatsapp || ''),
      contact_google_maps_url: String(t.contact_google_maps_url || ''),
      contact_facebook_url: String(t.contact_facebook_url || ''),
      contact_instagram_url: String(t.contact_instagram_url || ''),
      contact_youtube_url: String(t.contact_youtube_url || ''),
      contact_opening_hours: String(t.contact_opening_hours || ''),
      timezone: t.timezone != null && String(t.timezone).trim() ? String(t.timezone).trim() : '',
      software_house_id: t.software_house_id != null && String(t.software_house_id) !== '' ? String(t.software_house_id) : '',
      wa_sh_api_token: '',
      clearWaShToken: false,
    });
  }

  async function loadAdminTenantCredentials() {
    if (!editTenant) return;
    setAdminTenantCredentialsErr('');
    setAdminTenantCredentialsBusy(true);
    try {
      const d = await api(`/admin/tenants/${editTenant.id}/credentials`);
      setAdminTenantCredentials(d && typeof d === 'object' ? d : null);
    } catch (e) {
      setAdminTenantCredentials(null);
      setAdminTenantCredentialsErr(e?.message || 'Não foi possível carregar as credenciais.');
    } finally {
      setAdminTenantCredentialsBusy(false);
    }
  }

  async function copyAdminCredential(label, text) {
    const t = text == null ? '' : String(text);
    if (t === '') {
      setAdminTenantCredentialsErr(`«${label}» está vazio — nada para copiar.`);
      return;
    }
    try {
      await navigator.clipboard.writeText(t);
      setSuccessMsg(`${label} copiado para a área de transferência.`);
      setAdminTenantCredentialsErr('');
      window.setTimeout(() => setSuccessMsg(''), 2800);
    } catch {
      setAdminTenantCredentialsErr(`Não foi possível copiar «${label}» (permissão do navegador?).`);
    }
  }

  async function regenerateAdminTenantErpCredentials() {
    if (!editTenant) return;
    if (
      !window.confirm(
        'Será gerado um novo Client ID e um novo Secret ERP para esta loja. O par anterior deixa de funcionar no ERP (X-Auth-Id / X-Auth-Secret). Continuar?',
      )
    ) {
      return;
    }
    setEditTenantErr('');
    setAdminTenantCredentialsErr('');
    setAdminErpRegenBusy(true);
    try {
      const d = await api(`/admin/tenants/${editTenant.id}/erp-credentials`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const id = d?.erp_client_id != null ? String(d.erp_client_id) : '';
      const secret = d?.erp_client_secret != null ? String(d.erp_client_secret) : '';
      setAdminErpRegenerated({ id, secret });
      setAdminTenantCredentials(null);
      setEditTenantForm((f) => ({
        ...f,
        erp_client_id: id,
        erp_client_secret: '',
        clearErpSecret: false,
      }));
      setEditTenant((t) =>
        t ? { ...t, erp_client_id: id || null, erp_secret_configured: secret ? 1 : 0 } : null,
      );
      setSuccessMsg('Novas credenciais ERP gravadas na base. Copie o Client ID e o Secret abaixo (ou use «Carregar credenciais»).');
      window.setTimeout(() => setSuccessMsg(''), 5000);
      await reload();
    } catch (e) {
      setEditTenantErr(e?.message || 'Erro ao gerar credenciais ERP.');
    } finally {
      setAdminErpRegenBusy(false);
    }
  }

  async function saveEditUser(e) {
    e.preventDefault();
    if (!editUser) return;
    setEditUserErr('');
    setEditUserBusy(true);
    try {
      const origPlan = editUser.billing_plan === 'gold' ? 'gold' : 'essential_trial';
      const origTrialLocal = toDatetimeLocalValue(editUser.trial_ends_at);
      const body = {
        name: editUserForm.name.trim(),
        email: editUserForm.email.trim().toLowerCase(),
        whatsapp: editUserForm.whatsapp.trim(),
        role: editUserForm.role,
        blocked: editUserForm.blocked,
      };
      if (editUserForm.billing_plan !== origPlan) {
        body.billing_plan = editUserForm.billing_plan;
      }
      if (editUserForm.billing_plan === 'essential_trial') {
        if (editUserForm.trial_ends_at !== origTrialLocal) {
          body.trial_ends_at = editUserForm.trial_ends_at
            ? new Date(editUserForm.trial_ends_at).toISOString()
            : null;
        }
      }
      if (editUserForm.password.trim()) {
        body.password = editUserForm.password.trim();
      }
      await api(`/admin/users/${editUser.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setEditUser(null);
      await reload();
    } catch (err) {
      setEditUserErr(err.message || 'Erro ao gravar');
    } finally {
      setEditUserBusy(false);
    }
  }

  async function saveEditTenant(e) {
    e.preventDefault();
    if (!editTenant) return;
    setEditTenantErr('');
    const nomeTrim = editTenantForm.nome.trim();
    const slugNorm = toSlug(editTenantForm.slug.trim());
    const uid = Number(editTenantForm.user_id);
    if (nomeTrim.length < 2) {
      setEditTenantErr('Nome da empresa muito curto.');
      return;
    }
    if (!slugNorm || isReservedStoreSlug(slugNorm)) {
      setEditTenantErr('Slug inválido ou reservado.');
      return;
    }
    if (!Number.isFinite(uid) || uid < 1) {
      setEditTenantErr('User ID do dono inválido.');
      return;
    }
    const pkSaaS = editTenantForm.subscription_plan_key.trim() || 'gold';
    let ovrVal = null;
    if (editTenantForm.subscription_price_override_brl.trim() !== '') {
      const parsed = parseLooseBRLMoney(editTenantForm.subscription_price_override_brl);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setEditTenantErr('Preço SaaS por empresa inválido (ex.: 197 ou 197,90).');
        return;
      }
      ovrVal = Math.round(parsed * 100) / 100;
    }
    let discountPct = null;
    if (editTenantForm.subscription_discount_percent.trim() !== '') {
      const parsedDp = parseLooseBRLMoney(editTenantForm.subscription_discount_percent);
      if (!Number.isFinite(parsedDp) || parsedDp < 1 || parsedDp > 50) {
        setEditTenantErr('Desconto SaaS inválido. Use de 1% a 50%.');
        return;
      }
      discountPct = Math.round(parsedDp * 100) / 100;
    }
    setEditTenantBusy(true);
    try {
      const body = {
        nome: nomeTrim,
        slug: slugNorm,
        user_id: uid,
        status: editTenantForm.status,
        payment_online_enabled: editTenantForm.payment_online_enabled,
        block_customer_public_registration: editTenantForm.block_customer_public_registration,
        require_adult_confirmation: editTenantForm.require_adult_confirmation,
        catalog_only_mode: editTenantForm.catalog_only_mode,
        catalog_no_photo_mode: editTenantForm.catalog_no_photo_mode,
        block_sale_zero_stock: editTenantForm.block_sale_zero_stock,
        pickup_only_mode: editTenantForm.pickup_only_mode,
        erp_client_id: editTenantForm.erp_client_id.trim() === '' ? null : editTenantForm.erp_client_id.trim(),
        subscription_plan_key: pkSaaS,
        subscription_price_override_brl: ovrVal,
        subscription_discount_percent: discountPct,
        subscription_billing_exempt_erp: editTenantForm.subscription_billing_exempt_erp,
        contact_address: editTenantForm.contact_address.trim() || null,
        contact_whatsapp: editTenantForm.contact_whatsapp.trim() || null,
        contact_google_maps_url: editTenantForm.contact_google_maps_url.trim() || null,
        contact_facebook_url: editTenantForm.contact_facebook_url.trim() || null,
        contact_instagram_url: editTenantForm.contact_instagram_url.trim() || null,
        contact_youtube_url: editTenantForm.contact_youtube_url.trim() || null,
        contact_opening_hours: editTenantForm.contact_opening_hours.trim() || null,
      };
      const tzTrim = editTenantForm.timezone.trim();
      const prevTz = String(editTenant.timezone || '').trim();
      if (tzTrim !== prevTz) {
        body.timezone = tzTrim === '' ? null : tzTrim;
      }
      if (editTenantForm.clearErpSecret) {
        body.erp_client_secret = null;
      } else if (editTenantForm.erp_client_secret.trim() !== '') {
        body.erp_client_secret = editTenantForm.erp_client_secret.trim();
      }
      if (editTenantForm.clearMercadoPagoGateway) {
        body.gateway_mercado_pago = null;
      } else if (editTenantForm.mercadopago_access_token.trim() !== '') {
        body.gateway_mercado_pago = editTenantForm.mercadopago_access_token.trim();
      }
      const prevMpClientId = String(editTenant.mercadopago_client_id || '').trim();
      if (editTenantForm.mercadopago_client_id.trim() !== prevMpClientId) {
        body.mercadopago_client_id =
          editTenantForm.mercadopago_client_id.trim() === '' ? null : editTenantForm.mercadopago_client_id.trim();
      }
      if (editTenantForm.clearMercadoPagoOAuthSecret) {
        body.clear_mercadopago_oauth_secret = true;
      } else if (editTenantForm.mercadopago_client_secret.trim() !== '') {
        body.mercadopago_client_secret = editTenantForm.mercadopago_client_secret.trim();
      }
      if (editTenantForm.software_house_id === '' || editTenantForm.software_house_id === '0') {
        body.software_house_id = null;
      } else {
        body.software_house_id = Number(editTenantForm.software_house_id);
      }
      if (editTenantForm.clearWaShToken) {
        body.wa_sh_api_token = null;
      } else if (editTenantForm.wa_sh_api_token.trim() !== '') {
        body.wa_sh_api_token = editTenantForm.wa_sh_api_token.trim();
      }
      await api(`/admin/tenants/${editTenant.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      setEditTenant(null);
      await reload();
    } catch (err) {
      setEditTenantErr(err.message || 'Erro ao gravar');
    } finally {
      setEditTenantBusy(false);
    }
  }

  async function createUser(ev) {
    ev.preventDefault();
    setError('');
    try {
      await api('/admin/users', {
        method: 'POST',
        body: JSON.stringify(formUser),
      });
      setFormUser({ name: '', email: '', whatsapp: '', password: '', role: 'user' });
      await reload();
    } catch (e) {
      setError(e.message);
    }
  }

  async function createTenant(ev) {
    ev.preventDefault();
    setError('');
    try {
      const tid = Number(formTenant.user_id);
      if (!Number.isFinite(tid) || tid < 1) {
        setError('Escolha o utilizador responsável na lista (pesquise por nome, e-mail ou ID).');
        return;
      }
      const payload = formTenant.slug.trim()
        ? { user_id: tid, nome: formTenant.nome.trim(), slug: formTenant.slug.trim() }
        : { user_id: tid, nome: formTenant.nome.trim() };
      const dpRaw = String(formTenant.subscription_discount_percent || '').trim();
      if (dpRaw !== '') {
        const dp = parseLooseBRLMoney(dpRaw);
        if (!Number.isFinite(dp) || dp < 1 || dp > 50) {
          setError('Desconto SaaS inválido. Use de 1% a 50%.');
          return;
        }
        payload.subscription_discount_percent = Math.round(dp * 100) / 100;
      }
      payload.subscription_billing_exempt_erp = !!formTenant.subscription_billing_exempt_erp;

      await api('/admin/tenants', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setFormTenant({
        user_id: '',
        nome: '',
        slug: '',
        subscription_discount_percent: '',
        subscription_billing_exempt_erp: false,
      });
      setOwnerPickerQuery('');
      setOwnerPickerOpen(false);
      setOwnerPickRemoteList(null);
      setOwnerPickRemoteBusy(false);
      await reload();
    } catch (e) {
      setError(e.message);
    }
  }

  async function toggleBlockedUser(id, blocked) {
    await api(`/admin/users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ blocked }),
    });
    reload();
  }

  async function toggleBlockedTenant(id, status) {
    await api(`/admin/tenants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    reload();
  }

  async function saveBillingPlanDraftRow(planKey) {
    const draft = planDraftByKey[planKey];
    if (!draft) return;
    setPlanRowSaveBusy(planKey);
    setError('');
    try {
      const mn = parseLooseBRLMoney(draft.monthly_amount_brl);
      if (!Number.isFinite(mn) || mn < 0) {
        setError('Valor mensal do plano inválido.');
        return;
      }
      await api(`/admin/billing/plans/${encodeURIComponent(planKey)}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: draft.title.trim(),
          monthly_amount_brl: mn,
          lp_name: draft.lp_name.trim(),
          subtitle: draft.subtitle.trim(),
          price_label: draft.price_label.trim(),
          cadence: draft.cadence,
          bullets: String(draft.bulletsText || '')
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean),
          caveat: draft.caveat.trim() === '' ? null : draft.caveat.trim(),
          featured: Boolean(draft.featured),
        }),
      });
      setSuccessMsg(`Plano «${planKey}» atualizado.`);
      await reload();
    } catch (err) {
      const code = err?.data?.code;
      const msg = String(err?.message || '');
      if (code === 'SCHEMA_OUTDATED' || msg.includes('SCHEMA_OUTDATED')) {
        setError(
          'Schema desatualizado para o catálogo de planos. Importe database/0055_billing_plan_lp_copy.sql no MySQL e recarregue.',
        );
      } else {
        setError(err.message || 'Erro ao gravar plano');
      }
    } finally {
      setPlanRowSaveBusy('');
    }
  }

  async function adminGenerateSubscriptionCharge(tenantId) {
    setSaasGenBusyTenantId(tenantId);
    setError('');
    try {
      await api(`/admin/tenants/${tenantId}/subscription/generate-next`, { method: 'POST' });
      setSuccessMsg('Próximo recebimento gerado.');
      await reload();
    } catch (err) {
      setError(err.message || 'Não foi possível gerar a cobrança');
    } finally {
      setSaasGenBusyTenantId(null);
    }
  }

  const saasCatalogByKey = billingPlanMapFrom(saasPlans);
  const saasPlanOptions =
    Array.isArray(saasPlans) && saasPlans.length > 0
      ? saasPlans
      : [
          { plan_key: 'gold', title: 'Ouro (padrão)' },
          { plan_key: 'essential_trial', title: 'Essential / trial (0)' },
        ];

  return (
    <div className="space-y-8 py-8">
        <div className="space-y-2">
          <h2 className="font-display text-3xl font-semibold text-ink dark:text-mist">Painel global</h2>
          <p className="text-sm text-ink/70 dark:text-frost-muted">
            Gestão corporativa restrita ao perfil super_admin — comece pela visão geral e aprofunde por separador.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 rounded-3xl bg-black/5 p-2 dark:bg-white/[0.12]">
          {[
            ['overview', 'Visão geral'],
            ['finance', 'Financeiro'],
            ['plans', 'Planos'],
            ['tenants', 'Empresas'],
            ['users', 'Usuários'],
            ['templates', 'Templates'],
            ['logs', 'Logs'],
            ['erpLogs', 'Integração ERP'],
            ['softwareHouses', 'Software houses'],
          ].map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`rounded-2xl px-4 py-2 text-xs font-semibold uppercase transition ${
                tab === id
                  ? 'bg-white text-ink shadow dark:bg-ink dark:text-mist'
                  : 'text-ink/65 dark:text-frost dark:hover:bg-white/[0.1]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="rounded-2xl border border-accent2/40 bg-accent2/15 px-4 py-3 text-sm font-semibold text-accent2">
            {error}
          </div>
        )}

        {successMsg && (
          <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm font-semibold text-emerald-800 dark:text-emerald-200">
            {successMsg}
          </div>
        )}

        {tab === 'overview' && <AdminOverviewSection onOpenTab={setTab} />}

        {tab === 'finance' && <AdminFinanceSection />}

        {tab === 'users' && (
          <div className="grid gap-6 lg:grid-cols-2">
            <form
              className="space-y-3 rounded-3xl border border-ink/10 bg-white/80 p-6 dark:border-white/10 dark:bg-white/5"
              onSubmit={createUser}
            >
              <h3 className="font-semibold">Novo usuário</h3>
              <input
                placeholder="Nome"
                required
                className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                value={formUser.name}
                onChange={(e) => setFormUser({ ...formUser, name: e.target.value })}
              />
              <input
                type="email"
                placeholder="Email"
                required
                className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                value={formUser.email}
                onChange={(e) => setFormUser({ ...formUser, email: e.target.value })}
              />
              <input
                placeholder="WhatsApp (com DDI)"
                required
                className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                value={formUser.whatsapp}
                onChange={(e) => setFormUser({ ...formUser, whatsapp: e.target.value })}
              />
              <input
                type="password"
                placeholder="Senha forte"
                required
                className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                value={formUser.password}
                onChange={(e) => setFormUser({ ...formUser, password: e.target.value })}
              />
              <select
                className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                value={formUser.role}
                onChange={(e) => setFormUser({ ...formUser, role: e.target.value })}
              >
                <option value="user">Cliente da plataforma</option>
                <option value="super_admin">Super admin</option>
              </select>
              <button className="w-full rounded-2xl bg-accent py-2 text-sm font-semibold text-white" type="submit">
                Cadastrar
              </button>
            </form>
            <div className="space-y-3 rounded-3xl border border-ink/10 bg-white/80 p-6 dark:border-white/10 dark:bg-white/5">
              <h3 className="font-semibold">Usuários ativos</h3>
              <ul className="space-y-2 text-sm">
                {users.map((u) => (
                  <li
                    key={u.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-ink/5 px-3 py-3 dark:border-white/10"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{u.name}</div>
                      <div className="text-xs text-ink/50 dark:text-frost-muted">{u.email}</div>
                      <div className="text-xs text-ink/50 dark:text-frost-muted">WhatsApp: {u.whatsapp || '—'}</div>
                      <div className="mt-1 text-[10px] font-medium uppercase tracking-wide text-ink/50 dark:text-frost-muted">
                        #{u.id} · {u.role} · {u.billing_plan || '—'}
                        {u.blocked ? ' · bloqueado' : ''}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openEditUser(u)}
                        className="rounded-full border border-ink/20 px-3 py-1 text-xs font-semibold dark:border-white/25"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleBlockedUser(u.id, !u.blocked)}
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          u.blocked ? 'bg-accent/10 text-accent' : 'border border-accent2/60 text-accent2'
                        }`}
                      >
                        {u.blocked ? 'Desbloquear' : 'Bloquear'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {tab === 'templates' && (
          <AdminTemplatesSection
            users={users}
            onError={(msg) => setError(msg)}
            onSuccess={(msg) => {
              setError('');
              setSuccessMsg(msg);
            }}
          />
        )}

        {tab === 'softwareHouses' && (
          <div className="space-y-6 rounded-3xl border border-ink/10 bg-white/80 p-6 dark:border-white/10 dark:bg-white/5">
            <div>
              <h3 className="font-semibold">Software houses (gateway WhatsApp)</h3>
              <p className="mt-1 max-w-2xl text-[11px] text-ink/55 dark:text-frost-muted">
                Cada registo define um URL de API e um token por defeito. Nas empresas, escolha a SH e (recomendado) o
                token próprio do cliente — é esse Bearer que o sistema envia nos pedidos de WhatsApp dos pedidos. Opcionalmente
                configure o Access Token Mercado Pago da SH para que as mensalidades SaaS das lojas ligadas caiam na conta
                da SH (100%); sem esse token, a cobrança continua na conta global da plataforma.
              </p>
            </div>
            <form
              className="grid gap-3 rounded-2xl border border-ink/10 p-4 dark:border-white/10 md:grid-cols-3"
              onSubmit={async (ev) => {
                ev.preventDefault();
                setShBusy(true);
                setError('');
                try {
                  /** @type{Record<string, string>} */
                  const body = {
                    nome: shForm.nome.trim(),
                    endpoint_url: shForm.endpoint_url.trim(),
                    bearer_token: shForm.bearer_token.trim(),
                  };
                  if (shForm.mercadopago_access_token.trim() !== '') {
                    body.mercadopago_access_token = shForm.mercadopago_access_token.trim();
                  }
                  await api('/admin/software-houses', {
                    method: 'POST',
                    body: JSON.stringify(body),
                  });
                  setShForm({
                    nome: '',
                    endpoint_url: '',
                    bearer_token: '',
                    mercadopago_access_token: '',
                  });
                  setSuccessMsg('Software House criada.');
                  const sh = await api('/admin/software-houses');
                  setSoftwareHouses(sh.software_houses || []);
                } catch (e) {
                  setError(e.message || 'Erro ao criar');
                } finally {
                  setShBusy(false);
                }
              }}
            >
              <input
                required
                placeholder="Nome (ex. ACME Integrações)"
                className="rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                value={shForm.nome}
                onChange={(e) => setShForm({ ...shForm, nome: e.target.value })}
              />
              <input
                required
                placeholder="https://api.exemplo.com/messages/send"
                className="rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                value={shForm.endpoint_url}
                onChange={(e) => setShForm({ ...shForm, endpoint_url: e.target.value })}
              />
              <input
                placeholder="Token padrão WA da SH (opcional)"
                className="rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                value={shForm.bearer_token}
                onChange={(e) => setShForm({ ...shForm, bearer_token: e.target.value })}
              />
              <input
                placeholder="Access Token MP (mensalidades, opcional)"
                className="rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a] md:col-span-2"
                value={shForm.mercadopago_access_token}
                onChange={(e) => setShForm({ ...shForm, mercadopago_access_token: e.target.value })}
              />
              <div className="md:col-span-3">
                <button
                  type="submit"
                  disabled={shBusy}
                  className="rounded-2xl bg-ink px-4 py-2 text-sm font-semibold text-mist disabled:opacity-50 dark:bg-mist dark:text-ink"
                >
                  {shBusy ? 'A gravar…' : 'Adicionar Software House'}
                </button>
              </div>
            </form>
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3">
              {(softwareHouses || []).length === 0 ? (
                <li className="col-span-full text-sm text-ink/50">Nenhuma Software House cadastrada.</li>
              ) : (
                (softwareHouses || []).map((sh) => (
                  <li
                    key={sh.id}
                    className="flex min-w-0 flex-col gap-2 rounded-2xl border border-ink/10 px-4 py-3 text-sm dark:border-white/10"
                  >
                    <div className="font-semibold">{sh.nome}</div>
                    <div className="break-all font-mono text-[11px] text-ink/70 dark:text-frost-muted">{sh.endpoint_url}</div>
                    <div className="text-[10px] text-ink/50">
                      Token WA: {Number(sh.bearer_token_configured) === 1 ? 'configurado' : '—'}
                      {' · '}
                      MP mensalidade: {Number(sh.mercadopago_token_configured) === 1 ? 'configurado' : '—'}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="rounded-xl border border-ink/20 px-3 py-1 text-xs font-semibold text-ink dark:border-white/20 dark:text-mist"
                        onClick={() => {
                          setError('');
                          setEditSh({
                            id: Number(sh.id),
                            nome: String(sh.nome ?? ''),
                            endpoint_url: String(sh.endpoint_url ?? ''),
                            bearer_token: '',
                            bearer_was_configured: Number(sh.bearer_token_configured) === 1 ? 1 : 0,
                            mercadopago_access_token: '',
                            mp_was_configured: Number(sh.mercadopago_token_configured) === 1 ? 1 : 0,
                            clearMpToken: false,
                          });
                        }}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="rounded-xl border border-ink/20 px-3 py-1 text-xs font-semibold text-accent2 dark:border-white/20"
                        onClick={async () => {
                          if (!window.confirm(`Apagar Software House «${sh.nome}»?`)) return;
                          setError('');
                          try {
                            await api(`/admin/software-houses/${sh.id}`, { method: 'DELETE' });
                            setSuccessMsg('Software House removida.');
                            const r = await api('/admin/software-houses');
                            setSoftwareHouses(r.software_houses || []);
                          } catch (e) {
                            setError(e.message || 'Não foi possível apagar');
                          }
                        }}
                      >
                        Apagar
                      </button>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}

        {tab === 'plans' && (
          <div className="space-y-6">
            <div className="rounded-3xl border border-ink/10 bg-white/80 p-6 dark:border-white/10 dark:bg-white/5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold">Catálogo de planos SaaS</h3>
                  <p className="mt-1 max-w-xl text-[11px] text-ink/55 dark:text-frost-muted">
                    Valores e textos da landing/cadastro — alterar aqui atualiza a LP pública e a mensalidade padrão.
                    Cada empresa pode ter valor personalizado no modal «Editar» em Empresas.
                  </p>
                </div>
              </div>
              {saasPlans.length === 0 ? (
                <p className="mt-4 text-sm text-amber-800 dark:text-amber-200">
                  Certifique-se de que já importou <span className="font-mono">database/0001_schema.sql</span> e{' '}
                  <span className="font-mono">database/0055_billing_plan_lp_copy.sql</span> para o MySQL ou
                  recarregue após erro de API para carregar os planos.
                </p>
              ) : (
                <ul className="mt-5 space-y-4">
                  {saasPlans.map((p) => {
                    const dk = planDraftByKey[p.plan_key] ?? {
                      title: String(p.title ?? ''),
                      monthly_amount_brl: '',
                      lp_name: '',
                      subtitle: '',
                      price_label: '',
                      cadence: '',
                      bulletsText: '',
                      caveat: '',
                      featured: false,
                    };
                    const patchDraft = (partial) =>
                      setPlanDraftByKey((prev) => ({
                        ...prev,
                        [p.plan_key]: { ...dk, ...partial },
                      }));
                    const fieldClass =
                      'mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]';
                    return (
                      <li
                        key={p.plan_key}
                        className="rounded-2xl border border-ink/10 p-4 dark:border-white/10"
                      >
                        <div className="mb-3 font-mono text-[11px] font-semibold uppercase text-ink/55 dark:text-frost">
                          {p.plan_key}
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          <label className="block min-w-0 text-xs font-semibold text-ink dark:text-mist">
                            Título
                            <input
                              className={fieldClass}
                              value={dk.title}
                              onChange={(e) => patchDraft({ title: e.target.value })}
                            />
                          </label>
                          <label className="block min-w-0 text-xs font-semibold text-ink dark:text-mist">
                            R$ / mês
                            <input
                              className={`${fieldClass} font-mono`}
                              value={dk.monthly_amount_brl}
                              onChange={(e) => patchDraft({ monthly_amount_brl: e.target.value })}
                              inputMode="decimal"
                            />
                          </label>
                          <label className="block min-w-0 text-xs font-semibold text-ink dark:text-mist">
                            Nome LP
                            <input
                              className={fieldClass}
                              value={dk.lp_name}
                              onChange={(e) => patchDraft({ lp_name: e.target.value })}
                            />
                          </label>
                          <label className="block min-w-0 text-xs font-semibold text-ink dark:text-mist sm:col-span-2 lg:col-span-3">
                            Subtítulo
                            <input
                              className={fieldClass}
                              value={dk.subtitle}
                              onChange={(e) => patchDraft({ subtitle: e.target.value })}
                            />
                          </label>
                          <label className="block min-w-0 text-xs font-semibold text-ink dark:text-mist">
                            Price label
                            <input
                              className={fieldClass}
                              value={dk.price_label}
                              onChange={(e) => patchDraft({ price_label: e.target.value })}
                              placeholder="vazio = gera R$ mensal"
                            />
                            <span className="mt-1 block text-[10px] font-normal text-ink/45 dark:text-frost-muted">
                              Vazio = gera R$ mensal a partir do valor acima
                            </span>
                          </label>
                          <label className="block min-w-0 text-xs font-semibold text-ink dark:text-mist">
                            Cadence
                            <input
                              className={fieldClass}
                              value={dk.cadence}
                              onChange={(e) => patchDraft({ cadence: e.target.value })}
                            />
                          </label>
                          <label className="flex items-end gap-2 pb-2 text-xs font-semibold text-ink dark:text-mist">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-ink/20"
                              checked={Boolean(dk.featured)}
                              onChange={(e) => patchDraft({ featured: e.target.checked })}
                            />
                            Featured
                          </label>
                          <label className="block min-w-0 text-xs font-semibold text-ink dark:text-mist sm:col-span-2 lg:col-span-3">
                            Bullets
                            <textarea
                              className={fieldClass}
                              rows={5}
                              value={dk.bulletsText}
                              onChange={(e) => patchDraft({ bulletsText: e.target.value })}
                            />
                            <span className="mt-1 block text-[10px] font-normal text-ink/45 dark:text-frost-muted">
                              Uma linha = um bullet
                            </span>
                          </label>
                          <label className="block min-w-0 text-xs font-semibold text-ink dark:text-mist sm:col-span-2 lg:col-span-3">
                            Caveat
                            <input
                              className={fieldClass}
                              value={dk.caveat}
                              onChange={(e) => patchDraft({ caveat: e.target.value })}
                            />
                          </label>
                        </div>
                        <div className="mt-3 flex justify-end">
                          <button
                            type="button"
                            disabled={planRowSaveBusy === p.plan_key}
                            onClick={() => saveBillingPlanDraftRow(p.plan_key)}
                            className="rounded-2xl border border-ink/20 px-4 py-2 text-xs font-semibold dark:border-white/20"
                          >
                            {planRowSaveBusy === p.plan_key ? 'A gravar…' : 'Guardar plano'}
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === 'tenants' && (
          <div className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-2">
            <form
              className="space-y-3 rounded-3xl border border-ink/10 bg-white/80 p-6 dark:border-white/10 dark:bg-white/5"
              onSubmit={createTenant}
            >
              <h3 className="font-semibold">Criar empresa para um usuário</h3>
              <div ref={ownerPickerWrapRef} className="relative space-y-1">
                <label className="block text-xs font-semibold text-ink dark:text-mist" htmlFor="provision-owner-search">
                  Responsável (utilizador)
                </label>
                <p className="text-[10px] leading-snug text-ink/55 dark:text-frost-muted">
                  Pesquise por nome, e-mail, WhatsApp ou ID; escolha uma linha na lista. Com{' '}
                  <span className="font-semibold">2 ou mais caracteres</span> a pesquisa inclui até 200 utilizadores na
                  base (útil quando a lista local é grande).
                </p>
                {ownerPickRemoteBusy ? (
                  <p className="text-[10px] text-ink/45 dark:text-frost-muted">A consultar o servidor…</p>
                ) : null}
                <input
                  id="provision-owner-search"
                  type="search"
                  autoComplete="off"
                  spellCheck={false}
                  aria-expanded={ownerPickerOpen}
                  aria-controls="provision-owner-listbox"
                  aria-autocomplete="list"
                  placeholder="Digite para filtrar utilizadores…"
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                  value={ownerPickerQuery}
                  onChange={(e) => {
                    setOwnerPickerQuery(e.target.value);
                    setOwnerPickerOpen(true);
                  }}
                  onFocus={() => setOwnerPickerOpen(true)}
                />
                {ownerPickerOpen ? (
                  <ul
                    id="provision-owner-listbox"
                    role="listbox"
                    aria-label="Utilizadores que correspondem à pesquisa"
                    className="absolute left-0 right-0 z-50 mt-1 max-h-56 overflow-y-auto rounded-xl border border-ink/15 bg-white py-1 text-sm shadow-lg dark:border-white/15 dark:bg-[#161f1c]"
                  >
                    {ownerPickerFiltered.length === 0 ? (
                      <li className="px-3 py-2 text-xs text-ink/55 dark:text-frost-muted">
                        {users.length === 0
                          ? 'Ainda não há utilizadores na lista — crie utilizadores no separador «Usuários» ou recarregue esta página.'
                          : 'Nenhum utilizador corresponde a este filtro.'}
                      </li>
                    ) : (
                      ownerPickerFiltered.map((u) => (
                        <li key={u.id} role="presentation">
                          <button
                            type="button"
                            role="option"
                            aria-selected={String(formTenant.user_id) === String(u.id)}
                            className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-ink/[0.06] dark:hover:bg-white/[0.06]"
                            onClick={() => {
                              setFormTenant((prev) => ({ ...prev, user_id: String(u.id) }));
                              setOwnerPickerQuery('');
                              setOwnerPickerOpen(false);
                            }}
                          >
                            <span className="font-semibold text-ink dark:text-mist">{u.name || '—'}</span>
                            <span className="text-xs text-ink/65 dark:text-frost-muted">{u.email || '—'}</span>
                            <span className="font-mono text-[10px] text-ink/45 dark:text-frost-muted">
                              ID {u.id}
                              {u.role ? ` · ${u.role}` : ''}
                              {Number(u.blocked) === 1 ? ' · bloqueado' : ''}
                            </span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                ) : null}
              </div>
              {formTenant.user_id ? (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-600/30 bg-emerald-500/10 px-3 py-2 text-xs dark:border-emerald-500/25 dark:bg-emerald-950/25">
                  <div>
                    <span className="font-semibold text-emerald-950 dark:text-emerald-100">Selecionado:</span>{' '}
                    {provisionSelectedUser ? (
                      <>
                        {provisionSelectedUser.name} ({provisionSelectedUser.email}) ·{' '}
                        <span className="font-mono">ID {formTenant.user_id}</span>
                      </>
                    ) : (
                      <span className="font-mono">ID {formTenant.user_id}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="shrink-0 rounded-lg border border-ink/15 px-2 py-1 text-[11px] font-semibold dark:border-white/20"
                    onClick={() => {
                      setFormTenant((prev) => ({ ...prev, user_id: '' }));
                      setOwnerPickerQuery('');
                      setOwnerPickerOpen(false);
                    }}
                  >
                    Limpar
                  </button>
                </div>
              ) : null}
              <input
                placeholder="Nome da empresa"
                required
                className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                value={formTenant.nome}
                onChange={(e) => setFormTenant({ ...formTenant, nome: e.target.value })}
              />
              <input
                placeholder="Endereço da loja (opcional)"
                className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                value={formTenant.slug}
                onChange={(e) => setFormTenant({ ...formTenant, slug: e.target.value })}
              />
              <input
                placeholder="Desconto SaaS % (opcional, 1 a 50)"
                className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-ink"
                value={formTenant.subscription_discount_percent}
                onChange={(e) =>
                  setFormTenant({ ...formTenant, subscription_discount_percent: e.target.value })
                }
                inputMode="decimal"
              />
              <label className="flex items-center gap-2 text-sm font-semibold text-ink dark:text-mist">
                <input
                  type="checkbox"
                  checked={formTenant.subscription_billing_exempt_erp}
                  onChange={(e) =>
                    setFormTenant({ ...formTenant, subscription_billing_exempt_erp: e.target.checked })
                  }
                />
                Recebimento pelo ERP (não gerar mensalidades SaaS)
              </label>
              <button
                className="w-full rounded-2xl bg-ink px-4 py-2 text-sm font-semibold text-mist dark:bg-mist dark:text-ink"
                type="submit"
              >
                Provisionar empresa
              </button>
            </form>
            <div className="space-y-2 rounded-3xl border border-ink/10 bg-white/80 p-6 text-sm dark:border-white/10 dark:bg-white/5">
              <h3 className="font-semibold">Empresas</h3>
              <p className="text-xs text-ink/55 dark:text-frost-muted">
                Em cada linha o selo <span className="font-semibold text-emerald-800 dark:text-emerald-300">Operante</span> /{' '}
                <span className="font-semibold text-amber-800 dark:text-amber-200">Atenção</span> /{' '}
                <span className="font-semibold text-rose-800 dark:text-rose-200">Bloqueada</span> resume o estado crítico
                (bloqueio, ERP, SH/WhatsApp, MP). Use{' '}
                <span className="font-semibold text-sky-800 dark:text-sky-200">Relatório / PDF</span> para métricas e
                exportação.
              </p>
              <ul className="space-y-2">
                {tenants.map((t) => {
                  const health = resolveTenantOperationalHealth(t);
                  const rowRing =
                    health.level === 'bloqueada'
                      ? 'border-rose-500/35 bg-rose-500/[0.04]'
                      : health.level === 'atencao'
                        ? 'border-amber-500/30 bg-amber-500/[0.04]'
                        : 'border-ink/5 dark:border-white/10';
                  return (
                  <li
                    key={t.id}
                    className={`flex flex-col gap-3 rounded-xl border px-3 py-4 ${rowRing}`}
                  >
                    <div>
                      <div className="flex flex-wrap items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold">{t.nome}</div>
                          <div className="text-xs font-mono text-ink/50 dark:text-frost-muted">/{t.slug}</div>
                        </div>
                        <TenantOperationalBadge tenant={t} />
                      </div>
                      <div className="mt-1 text-[10px] text-ink/45 dark:text-frost-muted">
                        empresa #{t.id} · responsável ID {t.user_id}
                        {t.owner_email ? ` (${t.owner_email})` : ''}
                        {t.erp_client_id ? (
                          <span>
                            {' '}
                            · ID ERP <span className="font-mono">{String(t.erp_client_id).slice(0, 18)}…</span>
                          </span>
                        ) : null}
                        {Number(t.payment_online_enabled) === 1 ? (
                          <span>
                            {' '}
                            · <span className="font-semibold text-emerald-700 dark:text-emerald-400">pagamento online ativo</span>
                          </span>
                        ) : null}
                        {Number(t.mp_token_configured) === 1 ? (
                          <span>
                            {' '}
                            · token MP na empresa
                          </span>
                        ) : null}
                        {String(t.mercadopago_client_id || '').trim() !== '' ? (
                          <span>
                            {' '}
                            · MP OAuth Client ID por empresa
                          </span>
                        ) : null}
                        {Number(t.mp_oauth_secret_configured) === 1 ? (
                          <span>
                            {' '}
                            · OAuth MP (secret guardado)
                          </span>
                        ) : null}
                        {t.software_house_nome ? (
                          <span>
                            {' '}
                            · SH: <span className="font-semibold">{t.software_house_nome}</span>
                            {Number(t.wa_sh_api_token_configured) === 1 ? ' · token cliente OK' : ''}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-2 space-y-1 rounded-lg border border-violet-500/25 bg-violet-500/[0.06] px-2 py-2 text-[11px] text-ink/80 dark:text-mist">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className="font-semibold text-violet-900 dark:text-violet-200">Mensalidade SaaS</span>
                          <span className="font-mono">
                            plano {(t.subscription_plan_key && String(t.subscription_plan_key)) || 'padrão'}
                          </span>
                          {t.subscription_price_override_brl != null &&
                          String(t.subscription_price_override_brl).trim() !== '' ? (
                            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-900 dark:text-amber-100">
                              valor personalizado {formatBrlCompact(t.subscription_price_override_brl)}
                            </span>
                          ) : null}
                          {Number(t.subscription_discount_percent || 0) > 0 ? (
                            <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-900 dark:text-emerald-100">
                              desconto {Number(t.subscription_discount_percent)}%
                            </span>
                          ) : null}
                          {Number(t.subscription_billing_exempt_erp || 0) === 1 ? (
                            <span className="rounded-full bg-sky-500/20 px-2 py-0.5 text-[10px] font-semibold text-sky-900 dark:text-sky-100">
                              recebimento ERP
                            </span>
                          ) : null}
                          <span>{formatBrlCompact(tenantResolvedMonthlyAmount(t, saasCatalogByKey))}</span>
                          {Number(t.billing_overdue_blocked) === 1 ? (
                            <span className="rounded-full bg-accent2/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent2">
                              bloqueada por falta de pagamento
                            </span>
                          ) : null}
                        </div>
                        {Number(t.sub_pending_cnt) > 0 ? (
                          <div className="text-ink/65 dark:text-frost-muted">
                            Em aberto: <span className="font-semibold">{t.sub_pending_cnt}</span>{' '}
                            {formatBrlCompact(t.sub_pending_sum_brl)}{' '}
                            {t.sub_next_due_date ? (
                              <span>
                                · venc. <span className="font-mono">{String(t.sub_next_due_date).slice(0, 10)}</span>
                              </span>
                            ) : null}
                            <p className="mt-1 text-[10px] text-ink/55 dark:text-frost-muted">
                              Pagamento Mercado Pago no portal{' '}
                              <AdminSubscriptionPortalLink
                                tenantId={Number(t.id)}
                                payNow
                                className="font-semibold text-sky-800 underline dark:text-sky-200"
                              >
                                /app/mensalidade
                              </AdminSubscriptionPortalLink>{' '}
                              — acessível a quem opera o painel, sem depender do e-mail do dono.
                            </p>
                            {Number(t.sub_pending_cnt) > 0 ? (
                              <div className="mt-2">
                                <AdminSubscriptionPayButton
                                  tenantId={Number(t.id)}
                                  className="rounded-lg border border-sky-500/45 bg-white/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-sky-950 dark:bg-white/10 dark:text-sky-100"
                                  label="Abrir checkout MP"
                                />
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <div className="text-ink/55 dark:text-frost-muted">Sem cobrança pendente registada nesta empresa.</div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setReportTenant({ id: t.id, nome: t.nome, slug: t.slug })
                        }
                        className="rounded-full border border-sky-500/50 bg-sky-500/10 px-4 py-1 text-xs font-semibold text-sky-900 dark:border-sky-400/40 dark:bg-sky-500/10 dark:text-sky-100"
                      >
                        Relatório / PDF
                      </button>
                      <button
                        type="button"
                        onClick={() => openEditTenant(t)}
                        className="rounded-full border border-ink/20 px-4 py-1 text-xs font-semibold dark:border-white/25"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => openEditTenant(t)}
                        className="rounded-full border border-emerald-600/35 bg-emerald-500/10 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:border-emerald-400/35 dark:bg-emerald-500/10 dark:text-emerald-200"
                      >
                        Dados adicionais
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleBlockedTenant(t.id, t.status === 'blocked' ? 'active' : 'blocked')}
                        className="rounded-full border border-accent2 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-accent2"
                      >
                        {t.status === 'blocked' ? 'ativar' : 'bloquear empresa'}
                      </button>
                      <button
                        type="button"
                        onClick={() => openClearDataModal(t)}
                        className="rounded-full border border-amber-500/70 px-4 py-1 text-[11px] font-semibold uppercase text-amber-700 dark:text-amber-200"
                      >
                        limpar dados
                      </button>
                      <button
                        type="button"
                        disabled={
                          saasGenBusyTenantId != null &&
                          Number(saasGenBusyTenantId) === Number(t.id)
                          || Number(t.subscription_billing_exempt_erp || 0) === 1
                        }
                        onClick={() => adminGenerateSubscriptionCharge(t.id)}
                        className="rounded-full border border-emerald-600/50 px-4 py-1 text-[11px] font-semibold uppercase text-emerald-800 dark:text-emerald-300"
                      >
                        {saasGenBusyTenantId != null && Number(saasGenBusyTenantId) === Number(t.id)
                          ? 'A gerar…'
                          : 'Gerar próximo recebimento'}
                      </button>
                    </div>
                  </li>
                  );
                })}
              </ul>
            </div>
            </div>
          </div>
        )}

        {tab === 'logs' && (
          <div className="space-y-6">
            <div className="rounded-3xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4 text-xs dark:border-emerald-400/25 dark:bg-emerald-950/20">
              <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-sm tracking-tight text-emerald-950 dark:text-emerald-50">
                    WhatsApp / e-mail — pedidos
                  </h3>
                  <p className="mt-1 max-w-2xl text-[11px] leading-relaxed text-ink/60 dark:text-frost-muted">
                    Compra (checkout), acompanhamento (ERP) e mudanças de estado. Filtre pela loja (ex.:{' '}
                    <span className="font-mono">orangebarra</span>) para isolar os testes. Procure{' '}
                    <span className="font-mono">notify.checkout.dispatch</span> — se só aparecer{' '}
                    <span className="font-mono">phase=start</span> sem <span className="font-mono">done</span>, o envio
                    após o checkout não chegou a correr.
                  </p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <div ref={notifyLogTenantPickerRef} className="relative min-w-[14rem] max-w-xs">
                    <label className="flex flex-col text-[11px] font-semibold text-ink/80 dark:text-frost">
                      Loja
                      <input
                        type="text"
                        autoComplete="off"
                        placeholder="Nome ou slug…"
                        className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-2 py-1.5 text-xs dark:border-white/10 dark:bg-[#252f2a] dark:text-frost"
                        value={notifyLogTenantSearch}
                        onChange={(e) => {
                          setNotifyLogTenantSearch(e.target.value);
                          setNotifyLogTenantPickerOpen(true);
                        }}
                        onFocus={() => setNotifyLogTenantPickerOpen(true)}
                      />
                    </label>
                    {notifyLogTenantPickerOpen ? (
                      <div className="absolute left-0 right-0 z-40 mt-1 max-h-52 overflow-auto rounded-xl border border-ink/15 bg-white py-1 shadow-lg dark:border-white/15 dark:bg-[#1a2420]">
                        <button
                          type="button"
                          className="block w-full px-3 py-2 text-left text-[11px] text-ink/80 hover:bg-black/[0.06] dark:text-frost dark:hover:bg-white/[0.08]"
                          onClick={() => {
                            setNotifyLogTenantFilter('');
                            setNotifyLogTenantSearch('');
                            setNotifyLogTenantPickerOpen(false);
                          }}
                        >
                          Todas as lojas
                        </button>
                        {notifyLogTenantPickerList.slice(0, 80).map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            className="block w-full px-3 py-2 text-left text-[11px] hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
                            onClick={() => {
                              setNotifyLogTenantFilter(String(t.id));
                              setNotifyLogTenantSearch(`${t.nome ?? '—'} (#${t.id})`);
                              setNotifyLogTenantPickerOpen(false);
                            }}
                          >
                            <span className="font-medium text-ink dark:text-frost">{t.nome ?? '—'}</span>
                            <span className="ml-1 font-mono text-ink/50 dark:text-frost-muted">#{t.id}</span>
                            {t.slug ? (
                              <span className="mt-0.5 block truncate font-mono text-[10px] text-ink/45 dark:text-frost-muted">
                                {t.slug}
                              </span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <label className="flex flex-col text-[11px] font-semibold text-ink/80 dark:text-frost">
                    Linhas
                    <select
                      className="mt-1 rounded-xl border border-ink/10 bg-white px-2 py-1.5 font-mono text-xs dark:border-white/10 dark:bg-[#252f2a]"
                      value={notifyLogLimit}
                      onChange={(e) => setNotifyLogLimit(Number(e.target.value))}
                    >
                      {[50, 100, 200, 400, 800, 1200].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="rounded-2xl border border-ink/15 px-4 py-2 text-xs font-semibold dark:border-white/20"
                    onClick={() => reload()}
                  >
                    Atualizar
                  </button>
                </div>
              </div>
              {notifyLogsErr ? (
                <div className="mb-3 rounded-xl border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-950 dark:border-amber-400/40 dark:bg-amber-500/15 dark:text-amber-100">
                  {notifyLogsErr}
                </div>
              ) : null}
              <div className="max-h-[min(55vh,560px)] space-y-2 overflow-auto">
                {notifyLogs.map((l) => (
                  <pre
                    key={l.id}
                    className="whitespace-pre-wrap rounded-xl bg-black/[0.04] px-3 py-2 font-mono text-[11px] dark:bg-white/[0.04]"
                  >
                    {`${l.created_at} · ${l.tenant_nome ?? '—'}${l.tenant_slug ? ` (${l.tenant_slug})` : ''} · #${l.tenant_id ?? '—'}`}
                    {' · '}
                    <span className="font-semibold text-emerald-800 dark:text-emerald-300">{l.action}</span>
                    {l.meta ? `\n${formatNotifyLogMeta(l.meta)}` : ''}
                  </pre>
                ))}
                {!notifyLogs.length && (
                  <div className="text-ink/50 dark:text-frost-muted">
                    Nenhuma notificação registada ainda. Faça um pedido de teste e clique em Atualizar.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-ink/10 bg-white/80 p-4 text-xs dark:border-white/10 dark:bg-white/5">
              <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-sm tracking-tight">Últimos eventos (auditoria)</h3>
                  <p className="mt-1 max-w-xl text-[11px] text-ink/55 dark:text-frost-muted">
                    Ações gravadas em <span className="font-mono">audit_logs</span> (painel, empresas, templates,
                    etc.). Filtre por loja para reduzir ruído com várias empresas activas.
                  </p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <div ref={auditLogTenantPickerRef} className="relative min-w-[14rem] max-w-xs">
                    <label className="flex flex-col text-[11px] font-semibold text-ink/80 dark:text-frost">
                      Loja
                      <input
                        type="text"
                        autoComplete="off"
                        placeholder="Nome ou slug…"
                        className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-2 py-1.5 text-xs dark:border-white/10 dark:bg-[#252f2a] dark:text-frost"
                        value={auditLogTenantSearch}
                        onChange={(e) => {
                          setAuditLogTenantSearch(e.target.value);
                          setAuditLogTenantPickerOpen(true);
                        }}
                        onFocus={() => setAuditLogTenantPickerOpen(true)}
                      />
                    </label>
                    {auditLogTenantPickerOpen ? (
                      <div className="absolute left-0 right-0 z-40 mt-1 max-h-52 overflow-auto rounded-xl border border-ink/15 bg-white py-1 shadow-lg dark:border-white/15 dark:bg-[#1a2420]">
                        <button
                          type="button"
                          className="block w-full px-3 py-2 text-left text-[11px] text-ink/80 hover:bg-black/[0.06] dark:text-frost dark:hover:bg-white/[0.08]"
                          onClick={() => {
                            setAuditLogTenantFilter('');
                            setAuditLogTenantSearch('');
                            setAuditLogTenantPickerOpen(false);
                          }}
                        >
                          Todas as lojas
                        </button>
                        {auditLogTenantPickerList.slice(0, 80).map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            className="block w-full px-3 py-2 text-left text-[11px] hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
                            onClick={() => {
                              setAuditLogTenantFilter(String(t.id));
                              setAuditLogTenantSearch(`${t.nome ?? '—'} (#${t.id})`);
                              setAuditLogTenantPickerOpen(false);
                            }}
                          >
                            <span className="font-medium text-ink dark:text-frost">{t.nome ?? '—'}</span>
                            <span className="ml-1 font-mono text-ink/50 dark:text-frost-muted">#{t.id}</span>
                            {t.slug ? (
                              <span className="mt-0.5 block truncate font-mono text-[10px] text-ink/45 dark:text-frost-muted">
                                {t.slug}
                              </span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <label className="flex flex-col text-[11px] font-semibold text-ink/80 dark:text-frost">
                    Linhas
                    <select
                      className="mt-1 rounded-xl border border-ink/10 bg-white px-2 py-1.5 font-mono text-xs dark:border-white/10 dark:bg-[#252f2a]"
                      value={auditLogLimit}
                      onChange={(e) => setAuditLogLimit(Number(e.target.value))}
                    >
                      {[100, 250, 500, 1000, 1500, 2000, 2500].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="rounded-2xl border border-ink/15 px-4 py-2 text-xs font-semibold dark:border-white/20"
                    onClick={() => reload()}
                  >
                    Atualizar
                  </button>
                </div>
              </div>
              {logsTabAuditErr ? (
                <div className="mb-3 rounded-xl border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-950 dark:border-amber-400/40 dark:bg-amber-500/15 dark:text-amber-100">
                  {logsTabAuditErr}
                </div>
              ) : null}
              <div className="max-h-[min(70vh,720px)] space-y-2 overflow-auto">
                {logs.map((l) => (
                  <pre
                    key={l.id}
                    className="whitespace-pre-wrap rounded-xl bg-black/[0.04] px-3 py-2 font-mono text-[11px] dark:bg-white/[0.04]"
                  >
                    {`${l.created_at} · ${l.tenant_nome ?? '—'}${l.tenant_slug ? ` (${l.tenant_slug})` : ''} · #${l.tenant_id ?? '—'} · user ${l.actor_user_id ?? '—'} · ${l.action}`}
                    {l.meta ? ` → ${typeof l.meta === 'string' ? l.meta : JSON.stringify(l.meta)}` : ''}
                  </pre>
                ))}
                {!logs.length && <div className="text-ink/50 dark:text-frost-muted">Nenhuma entrada.</div>}
              </div>
            </div>

            <div className="rounded-3xl border border-ink/10 bg-white/80 p-4 text-xs dark:border-white/10 dark:bg-white/5">
              <h3 className="font-semibold text-sm tracking-tight">Integração PHP (amostra)</h3>
              <p className="mt-1 max-w-xl text-[11px] text-ink/55 dark:text-frost-muted">
                Últimas <span className="font-semibold">400</span> chamadas HTTP à API em{' '}
                <span className="font-mono">integration-php</span>. Para ver corpo completo, resposta e filtros por
                empresa, use o separador <span className="font-semibold">Integração ERP</span> (até 800 linhas).
              </p>
              {logsTabErpSchemaNote ? (
                <p className="mt-2 max-w-2xl rounded-xl border border-sky-500/35 bg-sky-500/10 px-3 py-2 text-[11px] leading-snug text-sky-950 dark:border-sky-400/30 dark:bg-sky-500/15 dark:text-sky-100">
                  {logsTabErpSchemaNote}
                </p>
              ) : null}
              {logsTabErpErr ? (
                <p className="mt-2 rounded-xl border border-accent2/40 bg-accent2/10 px-3 py-2 text-[11px] font-semibold text-accent2">
                  {logsTabErpErr}
                </p>
              ) : null}
              <div className="mt-3 max-h-[min(48vh,520px)] space-y-1.5 overflow-auto">
                {logsTabErpPreview.map((row) => (
                  <pre
                    key={row.id}
                    className="whitespace-pre-wrap rounded-xl bg-black/[0.04] px-3 py-2 font-mono text-[10px] leading-relaxed dark:bg-white/[0.04]"
                  >
                    <span className="text-ink/45 dark:text-frost-muted">
                      #{row.id} · {row.created_at ?? ''}
                    </span>
                    {' · '}
                    <span className="font-semibold text-accent">{row.http_method}</span> {row.route_path}
                    {row.query_string ? (
                      <span className="text-ink/45 dark:text-frost-muted"> ?{row.query_string}</span>
                    ) : null}
                    {' · '}
                    tenant {row.tenant_id}
                    {row.tenant_nome ? ` (${row.tenant_nome})` : ''}
                    {row.response_http_code != null && row.response_http_code !== '' ? (
                      <span className="text-ink/55 dark:text-frost-muted"> · HTTP {row.response_http_code}</span>
                    ) : null}
                    {row.duration_ms != null && row.duration_ms !== '' ? (
                      <span className="text-ink/55 dark:text-frost-muted"> · {row.duration_ms} ms</span>
                    ) : null}
                  </pre>
                ))}
                {!logsTabErpPreview.length && (
                  <div className="text-ink/50 dark:text-frost-muted">Sem registos de integração ou tabela em falta.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === 'erpLogs' && (
          <div className="space-y-4 rounded-3xl border border-ink/10 bg-white/80 p-6 dark:border-white/10 dark:bg-white/5">
            {erpInboundSchemaNote ? (
              <div className="rounded-xl border border-sky-500/35 bg-sky-500/10 px-4 py-3 text-xs font-medium leading-relaxed text-sky-950 dark:border-sky-400/30 dark:bg-sky-500/15 dark:text-sky-100">
                {erpInboundSchemaNote}
              </div>
            ) : null}
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="max-w-xl space-y-1">
                <h3 className="font-semibold tracking-tight">Recebimento na integração ERP</h3>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div ref={erpTenantPickerRef} className="relative min-w-[14rem] max-w-xs">
                  <label className="flex flex-col text-[11px] font-semibold text-ink/80 dark:text-frost">
                    Empresa
                    <input
                      type="text"
                      autoComplete="off"
                      placeholder="Pesquisar por nome ou slug…"
                      className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-2 py-1.5 text-xs dark:border-white/10 dark:bg-[#252f2a] dark:text-frost"
                      value={erpTenantSearch}
                      onChange={(e) => {
                        setErpTenantSearch(e.target.value);
                        setErpTenantPickerOpen(true);
                      }}
                      onFocus={() => setErpTenantPickerOpen(true)}
                    />
                  </label>
                  {erpTenantPickerOpen ? (
                    <div className="absolute left-0 right-0 z-40 mt-1 max-h-52 overflow-auto rounded-xl border border-ink/15 bg-white py-1 shadow-lg dark:border-white/15 dark:bg-[#1a2420]">
                      <button
                        type="button"
                        className="block w-full px-3 py-2 text-left text-[11px] text-ink/80 hover:bg-black/[0.06] dark:text-frost dark:hover:bg-white/[0.08]"
                        onClick={() => {
                          setErpTenantFilter('');
                          setErpTenantSearch('');
                          setErpTenantPickerOpen(false);
                        }}
                      >
                        Todas as empresas
                      </button>
                      {erpTenantPickerList.length === 0 ? (
                        <div className="px-3 py-2 text-[11px] text-ink/50 dark:text-frost-muted">
                          Nenhuma empresa corresponde à pesquisa.
                        </div>
                      ) : (
                        erpTenantPickerList.slice(0, 80).map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            className="block w-full px-3 py-2 text-left text-[11px] hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
                            onClick={() => {
                              setErpTenantFilter(String(t.id));
                              setErpTenantSearch(`${t.nome ?? '—'} (#${t.id})`);
                              setErpTenantPickerOpen(false);
                            }}
                          >
                            <span className="font-medium text-ink dark:text-frost">{t.nome ?? '—'}</span>
                            <span className="ml-1 font-mono text-ink/50 dark:text-frost-muted">#{t.id}</span>
                            {t.slug ? (
                              <span className="mt-0.5 block truncate font-mono text-[10px] text-ink/45 dark:text-frost-muted">
                                {t.slug}
                              </span>
                            ) : null}
                          </button>
                        ))
                      )}
                    </div>
                  ) : null}
                </div>
                <label className="flex flex-col text-[11px] font-semibold text-ink/80 dark:text-frost">
                  Método HTTP
                  <select
                    value={erpMethodFilter}
                    onChange={(e) => setErpMethodFilter(e.target.value)}
                    className="mt-1 min-w-[9rem] rounded-xl border border-ink/10 bg-white px-2 py-1.5 text-xs dark:border-white/10 dark:bg-[#252f2a] dark:text-frost"
                  >
                    <option value="">Todos</option>
                    {ERP_INBOUND_HTTP_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="rounded-2xl border border-ink/15 px-4 py-2 text-xs font-semibold dark:border-white/20"
                  onClick={() => reload()}
                >
                  Atualizar
                </button>
                <button
                  type="button"
                  className="rounded-2xl border border-accent2/50 px-4 py-2 text-xs font-semibold text-accent2"
                  onClick={async () => {
                    if (!window.confirm('Apagar todas as entradas do log ERP?')) return;
                    setError('');
                    try {
                      await api('/admin/erp-inbound-logs', { method: 'DELETE' });
                      await reload();
                    } catch (e) {
                      setError(e.message);
                    }
                  }}
                >
                  Limpar log
                </button>
              </div>
            </div>
            <div className="max-h-[58vh] space-y-2 overflow-auto text-xs">
              {erpInbound.map((row) => {
                const open = erpExpandedId === row.id;
                const pretty =
                  row.body_json != null
                    ? JSON.stringify(row.body_json, null, 2)
                    : row.body_raw || '(sem corpo)';
                const metaPretty =
                  row.meta_json != null
                    ? typeof row.meta_json === 'object'
                      ? JSON.stringify(row.meta_json, null, 2)
                      : String(row.meta_json)
                    : null;
                const respSnippet =
                  row.response_body != null && String(row.response_body).trim() !== ''
                    ? String(row.response_body).length > 120000
                      ? `${String(row.response_body).slice(0, 120000)}\n…`
                      : String(row.response_body)
                    : null;
                const code = row.response_http_code;
                const ms = row.duration_ms;
                return (
                  <div
                    key={row.id}
                    className="rounded-2xl border border-ink/10 bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.04]"
                  >
                    <button
                      type="button"
                      className="flex w-full flex-wrap items-baseline justify-between gap-2 px-3 py-2 text-left"
                      onClick={() => setErpExpandedId(open ? null : row.id)}
                    >
                      <span className="font-mono text-[11px] text-ink/90 dark:text-mist">
                        <span className="font-semibold text-accent">{row.http_method}</span> {row.route_path}
                        {row.query_string ? (
                          <span className="text-ink/50 dark:text-frost-muted">?{row.query_string}</span>
                        ) : null}
                        {code != null ? (
                          <span className="ml-1 rounded bg-ink/10 px-1.5 py-0.5 text-[10px] dark:bg-white/10">
                            HTTP {code}
                          </span>
                        ) : null}
                        {ms != null ? (
                          <span className="ml-1 text-[10px] text-ink/45 dark:text-frost-muted">{ms} ms</span>
                        ) : null}
                      </span>
                      <span className="text-[10px] text-ink/55 dark:text-frost/85">
                        #{row.id} · tenant {row.tenant_id}
                        {row.tenant_nome ? ` · ${row.tenant_nome}` : ''} · {row.created_at}
                      </span>
                    </button>
                    {open ? (
                      <div className="max-h-80 space-y-3 overflow-auto border-t border-ink/10 px-3 py-2 dark:border-white/10">
                        {metaPretty ? (
                          <div>
                            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink/50 dark:text-frost-muted">
                              Metadados do pedido
                            </div>
                            <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed">{metaPretty}</pre>
                          </div>
                        ) : null}
                        <div>
                          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink/50 dark:text-frost-muted">
                            Corpo do pedido
                          </div>
                          <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed">{pretty}</pre>
                        </div>
                        {respSnippet ? (
                          <div>
                            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-ink/50 dark:text-frost-muted">
                              Resposta (capturada no servidor)
                            </div>
                            <pre className="whitespace-pre-wrap font-mono text-[11px] leading-relaxed">{respSnippet}</pre>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
              {!erpInbound.length && (
                <div className="text-sm text-ink/50 dark:text-frost-muted">
                  Sem registos. Aguarde alguns minutos e volte a tentar.
                </div>
              )}
            </div>
          </div>
        )}

      {/* Modal editar Software House */}
      {editSh && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
          role="presentation"
          onClick={() => !shEditBusy && setEditSh(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-edit-sh-title"
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-ink/10 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-[#161f1c]"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 id="admin-edit-sh-title" className="font-display text-xl font-semibold text-ink dark:text-mist">
              Editar Software House
            </h3>
            <p className="mt-1 text-xs text-ink/55 dark:text-frost-muted">
              Altere o nome, o URL do endpoint, o token WhatsApp ou o Access Token Mercado Pago (mensalidades).
            </p>
            <form
              className="mt-4 space-y-3"
              onSubmit={async (ev) => {
                ev.preventDefault();
                if (!editSh) return;
                setShEditBusy(true);
                setError('');
                try {
                  /** @type{Record<string, string | boolean>} */
                  const body = {
                    nome: editSh.nome.trim(),
                    endpoint_url: editSh.endpoint_url.trim(),
                  };
                  if (editSh.bearer_token.trim() !== '') {
                    body.bearer_token = editSh.bearer_token.trim();
                  }
                  if (editSh.clearMpToken) {
                    body.clear_mercadopago_access_token = true;
                  } else if (editSh.mercadopago_access_token.trim() !== '') {
                    body.mercadopago_access_token = editSh.mercadopago_access_token.trim();
                  }
                  await api(`/admin/software-houses/${editSh.id}`, {
                    method: 'PATCH',
                    body: JSON.stringify(body),
                  });
                  setEditSh(null);
                  setSuccessMsg('Software House atualizada.');
                  const r = await api('/admin/software-houses');
                  setSoftwareHouses(r.software_houses || []);
                } catch (e) {
                  setError(e.message || 'Erro ao gravar');
                } finally {
                  setShEditBusy(false);
                }
              }}
            >
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Nome
                <input
                  required
                  minLength={2}
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editSh.nome}
                  onChange={(e) => setEditSh({ ...editSh, nome: e.target.value })}
                />
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                URL
                <input
                  required
                  type="url"
                  placeholder="https://…"
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editSh.endpoint_url}
                  onChange={(e) => setEditSh({ ...editSh, endpoint_url: e.target.value })}
                />
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Token padrão WA da SH (opcional)
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder={
                    editSh.bearer_was_configured === 1
                      ? '••• configurado — novo valor para substituir'
                      : 'deixe vazio para não definir'
                  }
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editSh.bearer_token}
                  onChange={(e) => setEditSh({ ...editSh, bearer_token: e.target.value })}
                />
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Access Token Mercado Pago (mensalidades)
                <span className="mt-0.5 block text-[11px] font-normal text-ink/55 dark:text-frost-muted">
                  Só para cobrança SaaS das lojas ligadas a esta SH. Não é o token WhatsApp/LikeZap.
                </span>
                <input
                  type="password"
                  autoComplete="new-password"
                  disabled={Boolean(editSh.clearMpToken)}
                  placeholder={
                    editSh.mp_was_configured === 1
                      ? '••• configurado — novo valor para substituir'
                      : 'deixe vazio para não definir (fallback = conta plataforma)'
                  }
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm disabled:opacity-50 dark:border-white/10 dark:bg-[#252f2a]"
                  value={editSh.mercadopago_access_token}
                  onChange={(e) =>
                    setEditSh({
                      ...editSh,
                      mercadopago_access_token: e.target.value,
                      clearMpToken: false,
                    })
                  }
                />
              </label>
              {editSh.mp_was_configured === 1 ? (
                <label className="flex items-center gap-2 text-xs text-ink/70 dark:text-frost-muted">
                  <input
                    type="checkbox"
                    checked={Boolean(editSh.clearMpToken)}
                    onChange={(e) =>
                      setEditSh({
                        ...editSh,
                        clearMpToken: e.target.checked,
                        mercadopago_access_token: e.target.checked ? '' : editSh.mercadopago_access_token,
                      })
                    }
                  />
                  Remover Access Token MP (voltar ao fallback da plataforma)
                </label>
              ) : null}
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="submit"
                  disabled={shEditBusy}
                  className="rounded-2xl bg-ink px-4 py-2 text-sm font-semibold text-mist disabled:opacity-50 dark:bg-mist dark:text-ink"
                >
                  {shEditBusy ? 'A gravar…' : 'Guardar'}
                </button>
                <button
                  type="button"
                  disabled={shEditBusy}
                  className="rounded-2xl border border-ink/20 px-4 py-2 text-sm font-semibold text-ink dark:border-white/20 dark:text-mist"
                  onClick={() => setEditSh(null)}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal editar utilizador */}
      {editUser && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
          role="presentation"
          onClick={() => !editUserBusy && setEditUser(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-edit-user-title"
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-ink/10 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-[#161f1c]"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 id="admin-edit-user-title" className="font-display text-xl font-semibold text-ink dark:text-mist">
              Editar cliente #{editUser.id}
            </h3>
            <p className="mt-1 text-xs text-ink/55 dark:text-frost-muted">Alterações gravadas na conta (e-mail único no sistema).</p>
            <form className="mt-4 space-y-3" onSubmit={saveEditUser}>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Nome
                <input
                  required
                  minLength={2}
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editUserForm.name}
                  onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                />
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                E-mail
                <input
                  type="email"
                  required
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editUserForm.email}
                  onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                />
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                WhatsApp (com DDI)
                <input
                  required
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editUserForm.whatsapp}
                  onChange={(e) => setEditUserForm({ ...editUserForm, whatsapp: e.target.value })}
                />
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Nova senha (opcional)
                <input
                  type="password"
                  autoComplete="new-password"
                  placeholder="vazio = manter a actual"
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editUserForm.password}
                  onChange={(e) => setEditUserForm({ ...editUserForm, password: e.target.value })}
                />
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Perfil
                <select
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editUserForm.role}
                  onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value })}
                >
                  <option value="user">Cliente</option>
                  <option value="super_admin">Super admin</option>
                </select>
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Plano de billing
                <select
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editUserForm.billing_plan}
                  onChange={(e) => setEditUserForm({ ...editUserForm, billing_plan: e.target.value })}
                >
                  <option value="essential_trial">Teste (Essential)</option>
                  <option value="gold">Ouro</option>
                </select>
              </label>
              {editUserForm.billing_plan === 'essential_trial' && (
                <label className="block text-sm font-semibold text-ink dark:text-mist">
                  Fim do teste
                  <input
                    type="datetime-local"
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                    value={editUserForm.trial_ends_at}
                    onChange={(e) => setEditUserForm({ ...editUserForm, trial_ends_at: e.target.value })}
                  />
                  <span className="mt-1 block text-[11px] font-normal text-ink/50 dark:text-frost-muted">
                    Se vazio ao gravar com plano teste, o servidor define +7 dias.
                  </span>
                </label>
              )}
              <label className="flex items-center gap-2 text-sm font-semibold text-ink dark:text-mist">
                <input
                  type="checkbox"
                  checked={editUserForm.blocked}
                  onChange={(e) => setEditUserForm({ ...editUserForm, blocked: e.target.checked })}
                />
                Conta bloqueada
              </label>
              {editUserErr && (
                <p className="rounded-xl border border-accent2/40 bg-accent2/10 px-3 py-2 text-xs font-semibold text-accent2">
                  {editUserErr}
                </p>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="button"
                  disabled={editUserBusy}
                  onClick={() => setEditUser(null)}
                  className="rounded-2xl border border-ink/20 px-4 py-2 text-sm font-semibold dark:border-white/20"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={editUserBusy}
                  className="rounded-2xl bg-accent px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {editUserBusy ? 'A gravar…' : 'Gravar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal editar empresa */}
      {editTenant && (
        <div
          className="fixed inset-0 z-50 flex min-h-0 items-center justify-center bg-black/55 p-3 backdrop-blur-sm sm:p-5 md:p-8"
          role="presentation"
          onClick={() => !editTenantBusy && setEditTenant(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-edit-tenant-title"
            className="max-h-[min(92vh,calc(100dvh-1.25rem))] w-full max-w-[calc(100vw-1.5rem)] overflow-y-auto overscroll-contain rounded-3xl border border-ink/10 bg-white p-4 shadow-2xl dark:border-white/10 dark:bg-[#161f1c] sm:max-w-2xl sm:p-6 md:max-w-3xl lg:max-w-4xl lg:p-8 xl:max-w-5xl"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 id="admin-edit-tenant-title" className="font-display text-xl font-semibold text-ink dark:text-mist">
              Editar empresa #{editTenant.id}
            </h3>
            <p className="mt-2 rounded-lg border border-amber-500/35 bg-amber-500/[0.08] px-3 py-2 text-[11px] leading-snug text-amber-950 dark:border-amber-400/30 dark:bg-amber-950/35 dark:text-amber-50">
              O layout modular da página inicial da loja (hero, carrosséis, JSON){' '}
              <strong className="font-semibold">não está neste modal Admin</strong>. Configure na conta do dono: menu{' '}
              <Link className="font-semibold underline underline-offset-2" to="/app/mensalidade">
                Mensalidade
              </Link>{' '}
              (<span className="font-mono">/app/mensalidade</span>) para pagamento; configurações em{' '}
              <Link className="font-semibold underline underline-offset-2" to="/app/empresa">
                Empresas
              </Link>{' '}
              → Editar empresa → aba{' '}
              <strong className="font-semibold">Parâmetros da vitrine</strong>.
            </p>
            <form className="mt-4 space-y-3" onSubmit={saveEditTenant}>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Nome da empresa
                <input
                  required
                  minLength={2}
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editTenantForm.nome}
                  onChange={(e) => setEditTenantForm({ ...editTenantForm, nome: e.target.value })}
                />
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Endereço da loja (URL pública)
                <input
                  required
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editTenantForm.slug}
                  onChange={(e) => setEditTenantForm({ ...editTenantForm, slug: e.target.value })}
                />
              </label>
              <div className="space-y-2 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] p-3">
                <p className="text-xs font-semibold text-emerald-900 dark:text-emerald-100">Dados adicionais da loja</p>
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Endereço"
                  value={editTenantForm.contact_address}
                  onChange={(e) => setEditTenantForm({ ...editTenantForm, contact_address: e.target.value })}
                />
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Telefone / WhatsApp"
                  value={editTenantForm.contact_whatsapp}
                  onChange={(e) => setEditTenantForm({ ...editTenantForm, contact_whatsapp: e.target.value })}
                />
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Link Google Maps"
                  value={editTenantForm.contact_google_maps_url}
                  onChange={(e) => setEditTenantForm({ ...editTenantForm, contact_google_maps_url: e.target.value })}
                />
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Facebook"
                  value={editTenantForm.contact_facebook_url}
                  onChange={(e) => setEditTenantForm({ ...editTenantForm, contact_facebook_url: e.target.value })}
                />
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Instagram"
                  value={editTenantForm.contact_instagram_url}
                  onChange={(e) => setEditTenantForm({ ...editTenantForm, contact_instagram_url: e.target.value })}
                />
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="YouTube"
                  value={editTenantForm.contact_youtube_url}
                  onChange={(e) => setEditTenantForm({ ...editTenantForm, contact_youtube_url: e.target.value })}
                />
                <input
                  className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  placeholder="Horário de funcionamento"
                  value={editTenantForm.contact_opening_hours}
                  onChange={(e) => setEditTenantForm({ ...editTenantForm, contact_opening_hours: e.target.value })}
                />
                <label className="block text-xs font-semibold text-ink/90 dark:text-mist">
                  Fuso horário (IANA — datas em WhatsApp / e-mail)
                  <input
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-xs dark:border-white/10 dark:bg-[#252f2a]"
                    list="ominia-admin-store-timezone-list"
                    placeholder="ex.: America/Sao_Paulo — vazio = OMINIA_DEFAULT_TIMEZONE"
                    value={editTenantForm.timezone}
                    onChange={(e) => setEditTenantForm({ ...editTenantForm, timezone: e.target.value })}
                    autoComplete="off"
                  />
                  <datalist id="ominia-admin-store-timezone-list">
                    {STORE_TIMEZONE_IANA_SUGGESTIONS.map((z) => (
                      <option key={z} value={z} />
                    ))}
                  </datalist>
                </label>
              </div>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Responsável
                <select
                  required
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editTenantForm.user_id}
                  onChange={(e) => setEditTenantForm({ ...editTenantForm, user_id: e.target.value })}
                >
                  <option value="">— selecionar utilizador —</option>
                  {editTenantForm.user_id &&
                  !(users || []).some((u) => String(u.id) === String(editTenantForm.user_id)) ? (
                    <option value={String(editTenantForm.user_id)}>
                      {editTenant?.owner_email
                        ? `Responsável actual (${editTenant.owner_email})`
                        : `Utilizador #${editTenantForm.user_id}`}
                    </option>
                  ) : null}
                  {(users || []).map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {u.name || 'Sem nome'} · {u.email}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Estado
                <select
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editTenantForm.status}
                  onChange={(e) => setEditTenantForm({ ...editTenantForm, status: e.target.value })}
                >
                  <option value="active">Ativa</option>
                  <option value="blocked">Bloqueada</option>
                </select>
              </label>
              <div className="space-y-2 rounded-2xl border border-sky-500/25 bg-sky-500/[0.06] p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-950 dark:text-sky-100">
                  WhatsApp · Software House
                </p>
                <label className="block text-xs font-semibold text-ink dark:text-mist">
                  Software House
                  <select
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                    value={editTenantForm.software_house_id}
                    onChange={(e) =>
                      setEditTenantForm({ ...editTenantForm, software_house_id: e.target.value })
                    }
                  >
                    <option value="">— nenhuma (global) —</option>
                    {(softwareHouses || []).map((sh) => (
                      <option key={sh.id} value={String(sh.id)}>
                        {sh.nome}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-semibold text-ink dark:text-mist">
                  Token API do cliente (Bearer)
                  <input
                    type="password"
                    autoComplete="new-password"
                    placeholder={
                      Number(editTenant.wa_sh_api_token_configured) === 1
                        ? '••• guardado — cole novo para substituir'
                        : 'cole o token fornecido pela SH'
                    }
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                    value={editTenantForm.wa_sh_api_token}
                    onChange={(e) =>
                      setEditTenantForm({ ...editTenantForm, wa_sh_api_token: e.target.value })
                    }
                  />
                </label>
                <label className="flex items-center gap-2 text-xs font-semibold text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    checked={editTenantForm.clearWaShToken}
                    onChange={(e) =>
                      setEditTenantForm({ ...editTenantForm, clearWaShToken: e.target.checked })
                    }
                  />
                  Remover token do cliente
                </label>
              </div>
              <div className="space-y-3 rounded-2xl border border-violet-500/20 bg-violet-500/[0.06] p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-950 dark:text-violet-100">
                  Mensalidade SaaS (por empresa)
                </p>
                <label className="block text-sm font-semibold text-ink dark:text-mist">
                  Plano (catálogo)
                  <select
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-[#252f2a]"
                    value={editTenantForm.subscription_plan_key}
                    onChange={(e) =>
                      setEditTenantForm({ ...editTenantForm, subscription_plan_key: e.target.value })
                    }
                  >
                    {saasPlanOptions.map((opt) => (
                      <option key={opt.plan_key} value={opt.plan_key}>
                        {opt.plan_key}
                        {opt.title ? ` · ${opt.title}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm font-semibold text-ink dark:text-mist">
                  Preço mensal personalizado (R$, opcional)
                  <input
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                    value={editTenantForm.subscription_price_override_brl}
                    onChange={(e) =>
                      setEditTenantForm({
                        ...editTenantForm,
                        subscription_price_override_brl: e.target.value,
                      })
                    }
                    placeholder="vazio = usar preço do catálogo"
                    inputMode="decimal"
                  />
                </label>
                <label className="block text-sm font-semibold text-ink dark:text-mist">
                  Desconto mensal (%) (opcional)
                  <input
                    className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                    value={editTenantForm.subscription_discount_percent}
                    onChange={(e) =>
                      setEditTenantForm({
                        ...editTenantForm,
                        subscription_discount_percent: e.target.value,
                      })
                    }
                    placeholder="vazio = sem desconto | 1 a 50"
                    inputMode="decimal"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm font-semibold text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    checked={editTenantForm.subscription_billing_exempt_erp}
                    onChange={(e) =>
                      setEditTenantForm({
                        ...editTenantForm,
                        subscription_billing_exempt_erp: e.target.checked,
                      })
                    }
                  />
                  Recebimento pelo ERP (não gerar mensalidades SaaS)
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-ink dark:text-mist">
                <input
                  type="checkbox"
                  checked={editTenantForm.payment_online_enabled}
                  onChange={(e) =>
                    setEditTenantForm({ ...editTenantForm, payment_online_enabled: e.target.checked })
                  }
                />
                Pagamento online activo
              </label>
              <label className="flex items-start gap-2 text-sm font-semibold text-ink dark:text-mist">
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0"
                  checked={editTenantForm.block_customer_public_registration}
                  onChange={(e) =>
                    setEditTenantForm({
                      ...editTenantForm,
                      block_customer_public_registration: e.target.checked,
                    })
                  }
                />
                <span>Bloquear cadastro público de cliente na loja (só login; novos clientes pelo painel ou ERP)</span>
              </label>
              <label className="flex items-start gap-2 text-sm font-semibold text-ink dark:text-mist">
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0"
                  checked={editTenantForm.require_adult_confirmation}
                  onChange={(e) =>
                    setEditTenantForm({
                      ...editTenantForm,
                      require_adult_confirmation: e.target.checked,
                    })
                  }
                />
                <span>
                  Vitrine com confirmação de maioridade (18+) — menores são orientados para uma página informativa.
                </span>
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-ink dark:text-mist">
                <input
                  type="checkbox"
                  checked={editTenantForm.catalog_only_mode}
                  onChange={(e) =>
                    setEditTenantForm({ ...editTenantForm, catalog_only_mode: e.target.checked })
                  }
                />
                Modo catálogo digital (sem compras)
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-ink dark:text-mist">
                <input
                  type="checkbox"
                  checked={editTenantForm.catalog_no_photo_mode}
                  onChange={(e) =>
                    setEditTenantForm({ ...editTenantForm, catalog_no_photo_mode: e.target.checked })
                  }
                />
                Catálogo sem foto nos cards
              </label>
              <label className="flex items-start gap-2 text-sm font-semibold text-ink dark:text-mist">
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0"
                  checked={editTenantForm.block_sale_zero_stock}
                  onChange={(e) =>
                    setEditTenantForm({ ...editTenantForm, block_sale_zero_stock: e.target.checked })
                  }
                />
                <span>
                  Não aceitar pedido quando o stock registado na loja for zero ou insuficiente (produtos permanecem
                  na vitrine).
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm font-semibold text-ink dark:text-mist">
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0"
                  checked={editTenantForm.pickup_only_mode}
                  onChange={(e) =>
                    setEditTenantForm({ ...editTenantForm, pickup_only_mode: e.target.checked })
                  }
                />
                <span>Só retirada na loja (sem entregas no checkout).</span>
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Mercado Pago Access Token por loja (opcional)
                <input
                  type="password"
                  autoComplete="new-password"
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editTenantForm.mercadopago_access_token}
                  onChange={(e) =>
                    setEditTenantForm({
                      ...editTenantForm,
                      mercadopago_access_token: e.target.value,
                      clearMercadoPagoGateway: false,
                    })
                  }
                  placeholder="vazio = não alterar"
                />
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-ink dark:text-mist">
                <input
                  type="checkbox"
                  checked={editTenantForm.clearMercadoPagoGateway}
                  onChange={(e) =>
                    setEditTenantForm({
                      ...editTenantForm,
                      clearMercadoPagoGateway: e.target.checked,
                      mercadopago_access_token: e.target.checked ? '' : editTenantForm.mercadopago_access_token,
                    })
                  }
                />
                Limpar token Mercado Pago desta empresa
              </label>
              {Number(editTenant.mp_token_configured) === 1 && (
                <p className="text-[11px] text-ink/50 dark:text-frost-muted">
                  Esta empresa tem token Mercado Pago guardado na base (valor oculto).
                </p>
              )}
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                MP OAuth · Client ID (por empresa)
                <input
                  type="text"
                  autoComplete="off"
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editTenantForm.mercadopago_client_id}
                  onChange={(e) =>
                    setEditTenantForm({ ...editTenantForm, mercadopago_client_id: e.target.value })
                  }
                  placeholder="vazio = apagar ID na base"
                />
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                MP OAuth · Client Secret (por empresa)
                <input
                  type="password"
                  autoComplete="new-password"
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editTenantForm.mercadopago_client_secret}
                  onChange={(e) =>
                    setEditTenantForm({
                      ...editTenantForm,
                      mercadopago_client_secret: e.target.value,
                      clearMercadoPagoOAuthSecret: false,
                    })
                  }
                  placeholder="vazio = não alterar"
                />
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-ink dark:text-mist">
                <input
                  type="checkbox"
                  checked={editTenantForm.clearMercadoPagoOAuthSecret}
                  onChange={(e) =>
                    setEditTenantForm({
                      ...editTenantForm,
                      clearMercadoPagoOAuthSecret: e.target.checked,
                      mercadopago_client_secret: e.target.checked ? '' : editTenantForm.mercadopago_client_secret,
                    })
                  }
                />
                Limpar Client Secret OAuth Mercado Pago desta empresa
              </label>
              {Number(editTenant.mp_oauth_secret_configured) === 1 && (
                <p className="text-[11px] text-ink/50 dark:text-frost-muted">
                  Secret OAuth MP já guardado (oculto); preencha só para substituir.
                </p>
              )}
              <div className="rounded-2xl border border-violet-500/30 bg-violet-500/[0.07] p-4 dark:border-violet-400/25 dark:bg-violet-950/25">
                <p className="text-xs font-semibold text-violet-950 dark:text-violet-100">Credenciais guardadas (ler e copiar)</p>
                <p className="mt-1 text-[11px] leading-relaxed text-ink/65 dark:text-frost-muted">
                  O servidor devolve os valores actuais da base e regista{' '}
                  <span className="font-mono">admin.tenant.credentials_viewed</span> na auditoria. Use «Carregar» antes
                  de copiar tokens ou secrets que não aparecem nos campos de edição acima. O botão verde gera um novo
                  par ERP (ID + secret) e invalida o anterior.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={editTenantBusy || adminTenantCredentialsBusy}
                    onClick={() => loadAdminTenantCredentials()}
                    className="rounded-xl border border-violet-600/40 bg-white/90 px-3 py-1.5 text-[11px] font-semibold text-violet-950 shadow-sm disabled:opacity-50 dark:border-violet-400/35 dark:bg-violet-950/40 dark:text-violet-50"
                  >
                    {adminTenantCredentialsBusy
                      ? 'A carregar…'
                      : adminTenantCredentials
                        ? 'Actualizar credenciais'
                        : 'Carregar credenciais'}
                  </button>
                  {adminTenantCredentials ? (
                    <button
                      type="button"
                      disabled={editTenantBusy || adminTenantCredentialsBusy}
                      onClick={() => {
                        setAdminTenantCredentials(null);
                        setAdminTenantCredentialsErr('');
                      }}
                      className="rounded-xl border border-ink/15 px-3 py-1.5 text-[11px] font-semibold dark:border-white/20"
                    >
                      Ocultar
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={editTenantBusy || adminTenantCredentialsBusy || adminErpRegenBusy}
                    onClick={() => regenerateAdminTenantErpCredentials()}
                    className="rounded-xl border border-emerald-700/40 bg-emerald-600/15 px-3 py-1.5 text-[11px] font-semibold text-emerald-950 disabled:opacity-50 dark:border-emerald-400/35 dark:bg-emerald-500/20 dark:text-emerald-100"
                  >
                    {adminErpRegenBusy ? 'A gerar…' : 'Gerar credenciais ERP (novo ID + secret)'}
                  </button>
                </div>
                {adminTenantCredentialsErr ? (
                  <p className="mt-2 text-[11px] font-medium text-accent2">{adminTenantCredentialsErr}</p>
                ) : null}
                {adminTenantCredentials ? (
                  <div className="mt-4 space-y-3 border-t border-violet-500/25 pt-3 text-[11px] dark:border-violet-400/20">
                    <p className="text-ink/70 dark:text-frost-muted">
                      <span className="font-semibold">{adminTenantCredentials.nome}</span>{' '}
                      <span className="font-mono text-ink/55 dark:text-frost-muted">/{adminTenantCredentials.slug}</span>
                      <span className="text-ink/45"> · id {adminTenantCredentials.tenant_id}</span>
                    </p>
                    <p className="text-[10px] leading-snug text-ink/55 dark:text-frost-muted">
                      URL típica do integrador PHP (cabeçalhos <span className="font-mono">X-Auth-Id</span> /{' '}
                      <span className="font-mono">X-Auth-Secret</span>):{' '}
                      <span className="break-all font-mono">{buildErpIntegrationBaseUrl()}</span>
                      {adminTenantCredentials.erp_base_url ? (
                        <>
                          {' '}
                          · override na empresa:{' '}
                          <span className="break-all font-mono">{String(adminTenantCredentials.erp_base_url)}</span>
                        </>
                      ) : null}
                    </p>
                    {[
                      ['erp_client_id', 'ERP · Client ID (X-Auth-Id)'],
                      ['erp_client_secret', 'ERP · Client secret (X-Auth-Secret)'],
                      ['gateway_mercado_pago', 'Mercado Pago · Access token (checkout)'],
                      ['mercadopago_client_id', 'MP OAuth · Client ID'],
                      ['mercadopago_client_secret', 'MP OAuth · Client secret'],
                      ['gateway_asaas', 'Asaas · token / chave'],
                      ['gateway_pixpdv', 'PixPDV · referência'],
                      ['wa_sh_api_token', 'Software house · API token'],
                    ].map(([key, lbl]) => {
                      const raw = adminTenantCredentials[key];
                      const s = raw == null ? '' : String(raw).trim();
                      return (
                        <div key={key} className="rounded-xl border border-ink/10 bg-white/90 p-2 dark:border-white/10 dark:bg-[#1a2420]">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-semibold text-ink dark:text-mist">{lbl}</span>
                            <button
                              type="button"
                              disabled={s === '' || editTenantBusy}
                              onClick={() => copyAdminCredential(lbl, s)}
                              className="shrink-0 rounded-lg border border-ink/15 px-2 py-0.5 text-[10px] font-semibold disabled:opacity-40 dark:border-white/20"
                            >
                              Copiar
                            </button>
                          </div>
                          {s ? (
                            <textarea
                              readOnly
                              rows={Math.min(6, Math.max(2, Math.ceil(s.length / 80)))}
                              className="mt-1.5 w-full resize-y rounded-lg border border-ink/10 bg-white px-2 py-1 font-mono text-[10px] leading-snug text-ink dark:border-white/10 dark:bg-ink dark:text-mist"
                              value={s}
                            />
                          ) : (
                            <p className="mt-1 text-[10px] text-ink/50 dark:text-frost-muted">— vazio na base</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              {adminErpRegenerated && (adminErpRegenerated.id || adminErpRegenerated.secret) ? (
                <div className="rounded-2xl border border-emerald-600/40 bg-emerald-500/[0.12] p-4 dark:border-emerald-400/35 dark:bg-emerald-950/30">
                  <p className="text-xs font-semibold text-emerald-950 dark:text-emerald-100">
                    Par ERP gerado e já gravado na base
                  </p>
                  <p className="mt-1 text-[10px] leading-relaxed text-ink/65 dark:text-frost-muted">
                    Copie para configurar o ERP. A acção fica registada como{' '}
                    <span className="font-mono">admin.tenant.erp_credentials_regenerated</span>.
                  </p>
                  <div className="mt-3 space-y-2">
                    <div className="rounded-xl border border-ink/10 bg-white/90 p-2 dark:border-white/10 dark:bg-[#1a2420]">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[11px] font-semibold text-ink dark:text-mist">Client ID (X-Auth-Id)</span>
                        <button
                          type="button"
                          disabled={!adminErpRegenerated.id || editTenantBusy}
                          onClick={() => copyAdminCredential('ERP Client ID', adminErpRegenerated.id)}
                          className="shrink-0 rounded-lg border border-ink/15 px-2 py-0.5 text-[10px] font-semibold dark:border-white/20"
                        >
                          Copiar
                        </button>
                      </div>
                      <textarea
                        readOnly
                        rows={2}
                        className="mt-1.5 w-full resize-y rounded-lg border border-ink/10 bg-white px-2 py-1 font-mono text-[10px] text-ink dark:border-white/10 dark:bg-ink dark:text-mist"
                        value={adminErpRegenerated.id}
                      />
                    </div>
                    <div className="rounded-xl border border-ink/10 bg-white/90 p-2 dark:border-white/10 dark:bg-[#1a2420]">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[11px] font-semibold text-ink dark:text-mist">Client secret (X-Auth-Secret)</span>
                        <button
                          type="button"
                          disabled={!adminErpRegenerated.secret || editTenantBusy}
                          onClick={() => copyAdminCredential('ERP Client secret', adminErpRegenerated.secret)}
                          className="shrink-0 rounded-lg border border-ink/15 px-2 py-0.5 text-[10px] font-semibold dark:border-white/20"
                        >
                          Copiar
                        </button>
                      </div>
                      <textarea
                        readOnly
                        rows={3}
                        className="mt-1.5 w-full resize-y rounded-lg border border-ink/10 bg-white px-2 py-1 font-mono text-[10px] text-ink dark:border-white/10 dark:bg-ink dark:text-mist"
                        value={adminErpRegenerated.secret}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                ERP Client ID (X-Auth-Id)
                <input
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editTenantForm.erp_client_id}
                  onChange={(e) => setEditTenantForm({ ...editTenantForm, erp_client_id: e.target.value })}
                  placeholder="vazio = apagar ID"
                />
              </label>
              <label className="block text-sm font-semibold text-ink dark:text-mist">
                Novo ERP Secret (opcional)
                <input
                  type="password"
                  autoComplete="new-password"
                  className="mt-1 w-full rounded-xl border border-ink/10 bg-white px-3 py-2 font-mono text-sm dark:border-white/10 dark:bg-[#252f2a]"
                  value={editTenantForm.erp_client_secret}
                  onChange={(e) =>
                    setEditTenantForm({
                      ...editTenantForm,
                      erp_client_secret: e.target.value,
                      clearErpSecret: false,
                    })
                  }
                  placeholder="vazio = não alterar"
                />
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-ink dark:text-mist">
                <input
                  type="checkbox"
                  checked={editTenantForm.clearErpSecret}
                  onChange={(e) =>
                    setEditTenantForm({
                      ...editTenantForm,
                      clearErpSecret: e.target.checked,
                      erp_client_secret: e.target.checked ? '' : editTenantForm.erp_client_secret,
                    })
                  }
                />
                Limpar secret ERP na base
              </label>
              {Number(editTenant.erp_secret_configured) === 1 && (
                <p className="text-[11px] text-ink/50 dark:text-frost-muted">
                  Esta empresa tem secret configurado (valor oculto).
                </p>
              )}
              {editTenantErr && (
                <p className="rounded-xl border border-accent2/40 bg-accent2/10 px-3 py-2 text-xs font-semibold text-accent2">
                  {editTenantErr}
                </p>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="button"
                  disabled={editTenantBusy}
                  onClick={() => setEditTenant(null)}
                  className="rounded-2xl border border-ink/20 px-4 py-2 text-sm font-semibold dark:border-white/20"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={editTenantBusy}
                  className="rounded-2xl bg-ink px-5 py-2 text-sm font-semibold text-mist disabled:opacity-50 dark:bg-mist dark:text-ink"
                >
                  {editTenantBusy ? 'A gravar…' : 'Gravar empresa'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {reportTenant && (
        <AdminTenantReportModal
          tenant={reportTenant}
          onClose={() => setReportTenant(null)}
          reportPathPrefix="admin/tenants"
          variant="admin"
        />
      )}

      {clearDataModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
          role="presentation"
          onClick={() => !clearDataBusy && closeClearDataModal()}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-clear-data-title"
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-amber-500/30 bg-white p-6 shadow-2xl dark:border-amber-500/25 dark:bg-[#161f1c]"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3
              id="admin-clear-data-title"
              className="font-display text-xl font-semibold text-amber-900 dark:text-amber-100"
            >
              Limpar dados da empresa
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-ink/70 dark:text-frost-muted">
              <span className="font-semibold">{clearDataModal.nome}</span>
              <span className="font-mono text-ink/55 dark:text-frost-muted"> #{clearDataModal.id}</span> — marca só o que
              queres remover. Operação irreversível.
            </p>
            <form className="mt-5 space-y-4" onSubmit={submitClearData}>
              <fieldset className="space-y-3 rounded-2xl border border-ink/10 p-4 dark:border-white/10">
                <legend className="px-1 text-xs font-bold uppercase tracking-wide text-ink/60 dark:text-frost-muted">
                  Escolher
                </legend>
                <label className="flex cursor-pointer gap-3 text-sm font-medium text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded"
                    checked={clearDataOpts.orders}
                    onChange={(e) => setClearDataOpts((o) => ({ ...o, orders: e.target.checked }))}
                  />
                  <span>
                    <span className="font-semibold">Pedidos</span> desta loja
                  </span>
                </label>
                <label className="flex cursor-pointer gap-3 text-sm font-medium text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded"
                    checked={clearDataOpts.customers}
                    onChange={(e) => setClearDataOpts((o) => ({ ...o, customers: e.target.checked }))}
                  />
                  <span>
                    <span className="font-semibold">Clientes cadastrados</span> só desta loja
                    <span className="mt-1 block text-[11px] font-normal text-ink/55 dark:text-frost-muted">
                      Remove primeiro os pedidos desta loja (obrigatório por causa da base de dados).
                      Clientes que partilham o mesmo e-mail ou telefone com alguém noutra loja que já tenha
                      pedidos <span className="font-semibold">nunca são apagados</span>, mesmo marcando esta
                      opção.
                    </span>
                  </span>
                </label>
                <label className="flex cursor-pointer gap-3 text-sm font-medium text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded"
                    checked={clearDataOpts.products}
                    onChange={(e) => setClearDataOpts((o) => ({ ...o, products: e.target.checked }))}
                  />
                  <span>
                    <span className="font-semibold">Produtos</span> e fotos locais gravadas pelo sistema
                  </span>
                </label>
              </fieldset>
              <label className="flex cursor-pointer gap-3 rounded-2xl border border-amber-600/35 bg-amber-500/10 p-4 text-xs font-semibold text-amber-950 dark:border-amber-500/35 dark:bg-amber-950/35 dark:text-amber-100">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded"
                  checked={clearDataConfirmIrreversible}
                  onChange={(e) => setClearDataConfirmIrreversible(e.target.checked)}
                />
                Confirmo que quero apagar os dados marcados e sei que não há como desfazer.
              </label>
              {clearDataOpts.customers && !clearDataOpts.orders ? (
                <p className="text-[11px] font-medium text-amber-800 dark:text-amber-200/90">
                  Ao limpar clientes, os pedidos desta empresa serão removidos automaticamente (mesmo que não tenhas
                  marcado “Pedidos”).
                </p>
              ) : null}
              {clearDataErr ? (
                <p className="rounded-xl border border-accent2/40 bg-accent2/10 px-3 py-2 text-xs font-semibold text-accent2">
                  {clearDataErr}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  disabled={clearDataBusy}
                  onClick={closeClearDataModal}
                  className="rounded-2xl border border-ink/20 px-4 py-2 text-sm font-semibold dark:border-white/20"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={
                    clearDataBusy ||
                    !clearDataConfirmIrreversible ||
                    !(clearDataOpts.orders || clearDataOpts.customers || clearDataOpts.products)
                  }
                  className="rounded-2xl bg-amber-600 px-5 py-2 text-sm font-bold text-white disabled:opacity-45 dark:bg-amber-700"
                >
                  {clearDataBusy ? 'A limpar…' : 'Limpar dados selecionados'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
